import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePacientes } from '../context/PatientsContext';
import {
  aprobarPlanillaProfesionalApi,
  asociarPacienteAgendaApi,
  crearConsultorioApi,
  eliminarConsultorioApi,
  guardarAsistenciasAgendaApi,
  guardarProfesionalAgendaApi,
  guardarTurnoAgendaApi,
  restaurarTurnosAgendaApi,
  importarAgendaApi,
  obtenerAgendaDiaApi,
  obtenerAgendaSemanaApi,
  obtenerPlanillaProfesionalApi,
  obtenerPlanillasProfesionalesApi,
  contarPlanillasPendientesApi,
  obtenerPlanillaDobleEntradaApi,
  obtenerScriptFormularioProfesionalesApi,
  obtenerEnviosFormularioApi,
  guardarEmailsProfesionalesApi,
  pedirEnvioFormulariosApi,
  cancelarEnvioFormulariosApi,
  obtenerSedesAgendaApi,
  obtenerSinAsociarAgendaApi,
  obtenerSinAutorizacionAgendaApi,
  rechazarPlanillaProfesionalApi,
  renombrarConsultorioApi,
  obtenerCatalogoProfesionalesApi,
  obtenerProfesionalesApi,
  eliminarPlanillaProfesionalApi,
  editarPlanillaProfesionalApi,
  cambiarDisciplinaPlanillaApi,
  obtenerValoresAgendaApi,
  guardarValoresAgendaApi,
} from '../services/api';
import {
  DIAS,
  DISCIPLINAS,
  DISCIPLINA_AGRUPADA,
  ESTADOS,
  NO_ENCONTRADO,
  claseDisciplina,
  claseTurno,
  fechaLocalIso,
  filasPlanillaDia,
  formatearFecha,
  nombreVisible,
  planillaDiaCsv,
  sumarDias,
} from '../utils/agenda';
import InputFecha from '../components/InputFecha';
import Ventana from '../components/Ventana';
import FormularioProfesional from '../components/FormularioProfesional';

const inputClass =
  'rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100';
const botonSecundario =
  'inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200';
const botonPrimario = 'rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50';
const tarjeta = 'rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800';

const normalizar = (valor) =>
  String(valor || '').trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

function nombrePacienteSistema(p) {
  return [p?.apellido, p?.nombre].filter(Boolean).join(', ') || p?.id || '';
}

function descargar(texto, nombre, tipo = 'text/csv;charset=utf-8') {
  const url = URL.createObjectURL(new Blob([texto], { type: tipo }));
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  a.click();
  URL.revokeObjectURL(url);
}

// Imprime solo la planilla (ver .agenda-print-area en index.css).
function imprimir() {
  document.body.classList.add('imprimiendo-agenda');
  const limpiar = () => {
    document.body.classList.remove('imprimiendo-agenda');
    window.removeEventListener('afterprint', limpiar);
  };
  window.addEventListener('afterprint', limpiar);
  window.print();
}

function Leyenda() {
  return (
    <div className="flex flex-wrap gap-2 text-xs print:hidden">
      {Object.entries(ESTADOS).map(([key, e]) => (
        <span key={key} className={`rounded-lg px-2 py-1 font-semibold ${e.clase}`}>{e.label}</span>
      ))}
      <span className={`rounded-lg px-2 py-1 font-semibold ${NO_ENCONTRADO.clase}`}>{NO_ENCONTRADO.label}</span>
      {DISCIPLINAS.map((d) => (
        <span key={d.key} className={`rounded-lg px-2 py-1 font-semibold ${d.clase}`}>{d.key}</span>
      ))}
    </div>
  );
}

function BuscadorPaciente({ pacientes, valor, alCambiar, etiqueta }) {
  const [texto, setTexto] = useState('');
  const opciones = useMemo(() => {
    const q = normalizar(texto);
    if (!q) return [];
    return pacientes
      .filter((p) => normalizar(`${p.apellido} ${p.nombre} ${p.dni}`).includes(q))
      .slice(0, 8);
  }, [pacientes, texto]);
  const elegido = valor == null ? null : pacientes.find((p) => String(p.id) === String(valor));
  return (
    <div className="space-y-1">
      {elegido ? (
        <div className="flex items-center justify-between gap-2 rounded-xl bg-emerald-50 px-3 py-2 text-sm dark:bg-emerald-900/30">
          <span className="font-semibold text-emerald-800 dark:text-emerald-200">{nombrePacienteSistema(elegido)}</span>
          <button type="button" className="text-xs font-bold text-rose-600" onClick={() => alCambiar(null)}>Quitar</button>
        </div>
      ) : (
        <>
          <input className={`${inputClass} w-full`} placeholder="Buscar paciente por apellido, nombre o DNI" aria-label={etiqueta}
            value={texto} onChange={(e) => setTexto(e.target.value)} />
          {opciones.length > 0 && (
            <ul className="max-h-40 overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-600">
              {opciones.map((p) => (
                <li key={p.id}>
                  <button type="button" className="w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700"
                    onClick={() => { alCambiar(p.id); setTexto(''); }}>
                    {nombrePacienteSistema(p)} {p.dni ? <span className="text-xs text-slate-400">DNI {p.dni}</span> : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function SelectorProfesional({ valor, alCambiar, profesionales, conservar = false }) {
  const registrado = !valor || profesionales.some((p) => p.nombre === valor);
  return (
    <>
      <select aria-label="Profesional" className={`${inputClass} w-full`} value={valor} onChange={(e) => alCambiar(e.target.value)}>
        <option value="">{conservar ? 'Conservar profesional actual' : 'Sin profesional'}</option>
        {!registrado && <option value={valor} disabled>{valor} (corregir)</option>}
        {profesionales.map((p) => <option key={p.id} value={p.nombre}>{p.nombre}</option>)}
      </select>
      {!profesionales.length && <p className="mt-1 text-xs text-slate-500">Un administrador puede agregar profesionales desde la pestaña Profesionales.</p>}
    </>
  );
}

// Cada fila de pacientes lleva una clave propia (no el índice), así sacar o
// agregar un paciente no mezcla el buscador ni el nombre de otra fila.
let siguienteFila = 0;
const nuevaFila = (datos) => ({ clave: `fila-${(siguienteFila += 1)}`, nombre: '', patientId: null, escrito: false, ...datos });
const mismoId = (a, b) => a != null && b != null && String(a) === String(b);

function EditorTurno({ turno, consultorio, pacientes, profesionales, alCerrar, alGuardar }) {
  const [estado, setEstado] = useState(turno.estado);
  const [profesional, setProfesional] = useState(turno.profesional || '');
  const [disciplina, setDisciplina] = useState(turno.disciplina || '');
  const [nota, setNota] = useState(turno.nota || '');
  // original: nombre tal como figura en la agenda (para los no cargados) y el
  // nombre del sistema que mandó el servidor (por si el paciente ya no está en la lista).
  const [lista, setLista] = useState(() => turno.pacientes.map((p) => nuevaFila({
    nombre: p.nombre, patientId: p.patientId || null, original: p.nombre, sistema: p.paciente || '',
  })));
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);
  const admitePacientes = estado === 'ocupado' || estado === 'reservado';
  const cambiarFila = (clave, cambios) => setLista((actual) => actual.map((x) => (x.clave === clave ? { ...x, ...cambios } : x)));
  const sacarFila = (clave) => setLista((actual) => actual.filter((x) => x.clave !== clave));
  const nombreDe = (fila) => {
    const sistema = pacientes.find((x) => mismoId(x.id, fila.patientId));
    if (sistema) return nombrePacienteSistema(sistema);
    return fila.patientId ? (fila.sistema || fila.nombre) : fila.nombre;
  };

  const guardar = async () => {
    setError('');
    const pacientesTurno = admitePacientes
      ? lista.map((fila) => ({ nombre: String(nombreDe(fila) || '').trim(), patientId: fila.patientId ? String(fila.patientId) : null }))
        .filter((p) => p.nombre)
      : [];
    const ids = pacientesTurno.filter((p) => p.patientId).map((p) => p.patientId);
    if (new Set(ids).size !== ids.length) {
      setError('El mismo paciente está dos veces en la casilla. Sacá uno de los dos.');
      return;
    }
    const nombres = pacientesTurno.filter((p) => !p.patientId).map((p) => normalizar(p.nombre));
    if (new Set(nombres).size !== nombres.length) {
      setError('El mismo nombre no cargado está dos veces en la casilla. Sacá uno de los dos.');
      return;
    }
    setGuardando(true);
    try {
      await alGuardar({ estado, profesional, disciplina, nota, pacientes: pacientesTurno });
    } catch (err) {
      setError(String(err?.message || 'No se pudo guardar el turno.'));
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" role="dialog" aria-modal="true" aria-label="Editar turno">
      <div className={`${tarjeta} max-h-[90vh] w-full max-w-lg space-y-4 overflow-y-auto p-5`}>
        <div>
          <p className="text-xs font-bold uppercase text-emerald-700">{consultorio}</p>
          <h3 className="text-lg font-extrabold text-slate-900 dark:text-white">
            {DIAS.find((d) => d.key === turno.dia)?.label} {turno.hora}
          </h3>
        </div>
        <label className="block text-xs font-bold text-slate-600 dark:text-slate-300">
          <span className="mb-1 block">Estado</span>
          <select className={`${inputClass} w-full`} value={estado} onChange={(e) => setEstado(e.target.value)}>
            {Object.entries(ESTADOS).map(([key, e]) => <option key={key} value={key}>{e.label}</option>)}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs font-bold text-slate-600 dark:text-slate-300">
            <span className="mb-1 block">Profesional</span>
            <SelectorProfesional valor={profesional} alCambiar={setProfesional} profesionales={profesionales} />
          </label>
          <label className="block text-xs font-bold text-slate-600 dark:text-slate-300">
            <span className="mb-1 block">Disciplina</span>
            <select className={`${inputClass} w-full`} value={disciplina} onChange={(e) => setDisciplina(e.target.value)}>
              <option value="">Sin indicar</option>
              {DISCIPLINAS.map((d) => <option key={d.key} value={d.key}>{d.key}</option>)}
              {disciplina === DISCIPLINA_AGRUPADA.key && <option value={disciplina}>{disciplina} (sin separar)</option>}
            </select>
          </label>
        </div>
        {!admitePacientes && lista.length > 0 && (
          <p className="text-xs text-amber-700">Con este estado la casilla no lleva pacientes: al guardar se sacan.</p>
        )}
        {admitePacientes && (
          <div className="space-y-3">
            <p className="text-xs font-bold text-slate-600 dark:text-slate-300">Pacientes</p>
            {lista.map((fila, i) => {
              const elegido = fila.patientId ? nombreDe(fila) : '';
              const noCargado = !fila.patientId && (fila.original || fila.escrito);
              return (
                <div key={fila.clave} className="space-y-2 rounded-xl border border-slate-200 p-3 dark:border-slate-600">
                  {fila.patientId ? (
                    <div className="flex items-center justify-between gap-2 rounded-xl bg-emerald-50 px-3 py-2 text-sm dark:bg-emerald-900/30">
                      <span className="font-semibold text-emerald-800 dark:text-emerald-200">{elegido}</span>
                      <button type="button" className="text-xs font-bold text-slate-600 dark:text-slate-300"
                        onClick={() => cambiarFila(fila.clave, { patientId: null })}>Cambiar</button>
                    </div>
                  ) : (
                    <>
                      {noCargado && (
                        <div className="flex items-center gap-2">
                          <input className={`${inputClass} flex-1`} value={fila.nombre} aria-label={`Nombre en la agenda ${i + 1}`}
                            onChange={(e) => cambiarFila(fila.clave, { nombre: e.target.value })} placeholder="Nombre como figura en la agenda" />
                          <span className={`rounded-lg px-2 py-1 text-[11px] font-bold ${NO_ENCONTRADO.clase}`}>No cargado</span>
                        </div>
                      )}
                      <BuscadorPaciente pacientes={pacientes} valor={null}
                        etiqueta={noCargado ? `Asociar paciente ${i + 1}` : `Paciente ${i + 1}`}
                        alCambiar={(patientId) => cambiarFila(fila.clave, { patientId })} />
                      {!noCargado && (
                        <button type="button" className="text-xs font-bold text-slate-600 underline dark:text-slate-300"
                          onClick={() => cambiarFila(fila.clave, { escrito: true })}>
                          No está cargado: escribir el nombre
                        </button>
                      )}
                    </>
                  )}
                  <button type="button" className="block text-xs font-bold text-rose-600" onClick={() => sacarFila(fila.clave)}>
                    Sacar del turno
                  </button>
                </div>
              );
            })}
            <button type="button" className={botonSecundario} onClick={() => setLista((actual) => [...actual, nuevaFila()])}>
              Agregar paciente
            </button>
          </div>
        )}
        <label className="block text-xs font-bold text-slate-600 dark:text-slate-300">
          <span className="mb-1 block">Nota</span>
          <input className={`${inputClass} w-full`} value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Por ejemplo: virtual" />
        </label>
        {error && <p role="alert" className="text-sm font-semibold text-rose-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className={botonSecundario} onClick={alCerrar}>Cancelar</button>
          <button type="button" className={botonPrimario} disabled={guardando} onClick={guardar}>Guardar</button>
        </div>
      </div>
    </div>
  );
}

const mismoProfesional = (a, b) => normalizar(a?.profesional || '') === normalizar(b?.profesional || '');

const FIN_TRAMO = 'tramo';

// Última hora del tramo que empieza en `desde` para un día: sigue mientras el
// profesional sea el mismo que en `desde` (aunque alguna hora tenga otro color).
function finTramo(turnos, dia, desde) {
  const delDia = turnos.filter((t) => t.dia === dia).sort((a, b) => a.hora.localeCompare(b.hora));
  const inicio = delDia.findIndex((t) => t.hora === desde);
  if (inicio < 0) return null;
  let fin = inicio;
  while (fin + 1 < delDia.length && mismoProfesional(delDia[fin + 1], delDia[inicio])) fin += 1;
  return delDia[fin].hora;
}

// Encabezado de profesional: nombre y disciplina (color) de un tramo de horas.
function EditorProfesional({ consultorio, inicial, horas, profesionales, alCerrar, alGuardar }) {
  const [dias, setDias] = useState(inicial.dias);
  const [desde, setDesde] = useState(inicial.desde);
  const [hasta, setHasta] = useState(inicial.hasta);
  const [profesional, setProfesional] = useState(inicial.profesional || '');
  // null = no cambiar el color.
  const [disciplina, setDisciplina] = useState(inicial.disciplina ?? null);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  const alternarDia = (key) => setDias((actual) => (actual.includes(key) ? actual.filter((d) => d !== key) : [...actual, key]));
  const guardar = async () => {
    if (!dias.length) { setError('Elegí al menos un día.'); return; }
    if (hasta !== FIN_TRAMO && desde > hasta) { setError('La hora de inicio tiene que ser anterior a la de fin.'); return; }
    // Solo se manda lo que cambió: el nombre vacío deja el que estaba.
    const cambios = {};
    if (profesional.trim() && profesional.trim() !== (inicial.profesional || '')) cambios.profesional = profesional.trim();
    if (disciplina !== null && disciplina !== (inicial.disciplina ?? null)) cambios.disciplina = disciplina;
    if (!Object.keys(cambios).length) { alCerrar(); return; }
    setGuardando(true);
    setError('');
    try {
      await alGuardar({ dias, desde, hasta, ...cambios });
    } catch (err) {
      setError(String(err?.message || 'No se pudo guardar el encabezado.'));
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" role="dialog" aria-modal="true" aria-label="Editar encabezado de profesional">
      <div className={`${tarjeta} w-full max-w-lg space-y-4 p-5`}>
        <div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-white">Encabezado de profesional</h3>
          <p className="text-xs text-slate-500">{consultorio}. Se aplica a las horas elegidas; lo que dejes sin tocar queda como está.</p>
        </div>
        <fieldset>
          <legend className="mb-1 text-xs font-bold text-slate-600 dark:text-slate-300">Días</legend>
          <div className="flex flex-wrap gap-2">
            {DIAS.map((d) => (
              <label key={d.key} className="inline-flex items-center gap-1 text-sm text-slate-700 dark:text-slate-200">
                <input type="checkbox" checked={dias.includes(d.key)} onChange={() => alternarDia(d.key)} />{d.label}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs font-bold text-slate-600 dark:text-slate-300">
            <span className="mb-1 block">Desde</span>
            <select className={`${inputClass} w-full`} value={desde} onChange={(e) => setDesde(e.target.value)}>
              {horas.map((h) => <option key={h} value={h}>{h}</option>)}
            </select>
          </label>
          <label className="text-xs font-bold text-slate-600 dark:text-slate-300">
            <span className="mb-1 block">Hasta</span>
            <select className={`${inputClass} w-full`} value={hasta} onChange={(e) => setHasta(e.target.value)}>
              <option value={FIN_TRAMO}>Hasta que cambie el profesional</option>
              {horas.map((h) => <option key={h} value={h}>{h}</option>)}
            </select>
          </label>
        </div>
        <label className="block text-xs font-bold text-slate-600 dark:text-slate-300">
          <span className="mb-1 block">Profesional</span>
          <SelectorProfesional valor={profesional} alCambiar={setProfesional} profesionales={profesionales} conservar />
        </label>
        <fieldset>
          <legend className="mb-1 text-xs font-bold text-slate-600 dark:text-slate-300">Color (disciplina)</legend>
          <div className="flex flex-wrap gap-2">
            {[{ key: '', clase: claseDisciplina('') }, ...DISCIPLINAS].map((d) => (
              <button key={d.key || 'ninguna'} type="button" aria-pressed={disciplina === d.key} onClick={() => setDisciplina(d.key)}
                className={`rounded-lg px-2 py-1 text-xs font-bold ${d.clase} ${disciplina === d.key ? 'ring-2 ring-emerald-600 ring-offset-2' : ''}`}>
                {d.key || 'Sin color'}
              </button>
            ))}
          </div>
        </fieldset>
        {error && <p className="text-sm font-semibold text-rose-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className={botonSecundario} onClick={alCerrar}>Cancelar</button>
          <button type="button" className={botonPrimario} disabled={guardando} onClick={guardar}>Guardar</button>
        </div>
      </div>
    </div>
  );
}


// Filtros de la agenda general. "Disponible" es un turno vacío del profesional;
// "Consultorio vacío" es una casilla sin profesional.
const FILTROS_ESTADO = [
  { key: 'disponible', label: 'Disponible (turno vacío)', coincide: (t) => t.estado === 'vacio' && !t.pacientes.length },
  { key: 'ocupado', label: 'Ocupado', coincide: (t) => t.pacientes.length > 0 },
  { key: 'reservado', label: 'Reservado', coincide: (t) => t.estado === 'reservado' && !t.pacientes.length },
  { key: 'almuerzo', label: 'Almuerzo', coincide: (t) => t.estado === 'almuerzo' && !t.pacientes.length },
  { key: 'no_disponible', label: 'Terapeuta no disponible', coincide: (t) => t.estado === 'no_disponible' && !t.pacientes.length },
  { key: 'sin_uso', label: 'Consultorio vacío', coincide: (t) => t.estado === 'sin_uso' && !t.pacientes.length },
];
const partesProfesional = (valor) => String(valor || '').split('/').map((p) => normalizar(p).trim()).filter(Boolean);
const SIN_PROFESIONAL = '__sin_profesional__';

function GrillaConsultorio({ consultorio, editable, alEditar, alEditarProfesional, coincide, conError, chica = false }) {
  const horas = Array.from(new Set(consultorio.turnos.map((t) => t.hora))).sort();
  const porClave = new Map(consultorio.turnos.map((t) => [`${t.dia}|${t.hora}`, t]));
  // Color del tramo: la primera disciplina cargada desde esa hora mientras siga el mismo profesional.
  const disciplinaTramo = (dia, desde) => {
    const inicio = porClave.get(`${dia}|${horas[desde]}`);
    for (let i = desde; i < horas.length; i += 1) {
      const t = porClave.get(`${dia}|${horas[i]}`);
      if (!mismoProfesional(t, inicio)) break;
      if (t?.disciplina) return t.disciplina;
    }
    return '';
  };
  return (
    <section className={`${tarjeta} overflow-hidden agenda-grupo`}>
      <header className="border-b border-slate-100 px-4 py-2 dark:border-slate-700">
        <h3 className="font-bold text-slate-900 dark:text-white">
          {consultorio.sede && <span className="mr-2 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-100">{consultorio.sede}</span>}
          {consultorio.nombre}
        </h3>
      </header>
      {/* Lunes a viernes entran sin desplazar; solo en pantallas chicas se desplaza. */}
      <div className="overflow-x-auto md:overflow-x-visible">
        <table className={`w-full table-fixed border-collapse md:min-w-0 ${chica ? 'min-w-[520px] text-[10px] leading-tight' : 'min-w-[640px] text-xs'}`}>
          <thead>
            <tr className="bg-violet-200 text-slate-800 dark:bg-violet-900/60 dark:text-slate-100">
              <th className="w-12 px-1 py-1">Hora</th>
              {DIAS.map((d) => <th key={d.key} className="px-1 py-1">{d.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {horas.map((hora, indice) => {
              const fila = DIAS.map((d) => porClave.get(`${d.key}|${hora}`));
              const anterior = indice > 0 ? DIAS.map((d) => porClave.get(`${d.key}|${horas[indice - 1]}`)) : [];
              // El nombre se escribe solo en los días en que cambia el profesional; en
              // los demás la casilla queda del color del tramo, sin repetirlo.
              // Solo cuenta el profesional: si cambia nada más el color (una hora
              // sin disciplina en medio del tramo), el nombre no se repite.
              const cambia = fila.map((t, i) => indice === 0 || !mismoProfesional(t, anterior[i]));
              const cambiaProfesional = cambia.some(Boolean);
              const tramo = (dia, t) => ({ dias: [dia], desde: hora, hasta: FIN_TRAMO, profesional: t?.profesional, disciplina: t?.disciplina || '' });
              return (
                <React.Fragment key={hora}>
                  {cambiaProfesional && (
                    <tr>
                      <td />
                      {fila.map((t, i) => (
                        <td key={i} className={`break-words border border-white p-0 text-center font-bold dark:border-slate-800 ${cambia[i] ? claseDisciplina(disciplinaTramo(DIAS[i].key, indice)) : ''}`}>
                          <button type="button" disabled={!editable} onClick={() => alEditarProfesional(tramo(DIAS[i].key, t), horas)}
                            aria-label={`Encabezado ${DIAS[i].label} ${hora}: ${t?.profesional || 'sin profesional'}`}
                            className={`block w-full px-1 py-0.5 font-bold ${editable ? 'hover:brightness-95' : 'cursor-default'}`}>
                            {cambia[i] ? (t?.profesional || '—') : ''}
                          </button>
                        </td>
                      ))}
                    </tr>
                  )}
                  <tr>
                    <td className="px-1 text-center font-mono font-semibold text-slate-500">
                      {editable ? (
                        <button type="button" title="Agregar encabezado de profesional desde esta hora"
                          aria-label={`Agregar encabezado desde las ${hora}`}
                          onClick={() => alEditarProfesional({ dias: DIAS.map((d) => d.key), desde: hora, hasta: FIN_TRAMO }, horas)}
                          className="w-full rounded hover:bg-slate-100 dark:hover:bg-slate-700">{hora.slice(0, 2)}</button>
                      ) : hora.slice(0, 2)}
                    </td>
                    {fila.map((t, i) => (
                      <td key={i} className={`break-words border border-white p-0 dark:border-slate-800 ${t && coincide && !coincide(t) ? 'opacity-20' : ''}`}>
                        {t ? (
                          <button
                            type="button"
                            disabled={!editable}
                            onClick={() => alEditar(t)}
                            title={conError?.has(t.id) ? 'Error de tratamiento: este paciente tiene el mismo área con otro profesional'
                              : t.pacientes.some((p) => !p.patientId) ? NO_ENCONTRADO.label : ESTADOS[t.estado]?.label}
                            data-error-tratamiento={conError?.has(t.id) ? 'true' : undefined}
                            className={`block h-full min-h-[2.25rem] w-full px-1 py-0.5 text-left leading-tight ${claseTurno(t)} ${conError?.has(t.id) ? 'outline outline-2 -outline-offset-2 outline-rose-700' : ''} ${editable ? 'hover:brightness-95' : 'cursor-default'}`}
                          >
                            {conError?.has(t.id) && <span aria-hidden="true" className="mr-0.5 font-bold text-rose-700">⚠</span>}
                            {t.estado === 'almuerzo' && !t.pacientes.length ? 'ALMUERZO' : t.pacientes.map(nombreVisible).join(' / ')}
                            {t.nota ? <span className="block text-[10px] opacity-75">({t.nota})</span> : null}
                          </button>
                        ) : null}
                      </td>
                    ))}
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// Clave de un paciente de la agenda: el paciente del sistema si está vinculado, si no el nombre escrito.
const clavePaciente = (p) => (p.patientId ? `id:${p.patientId}` : `nombre:${normalizar(p.nombre)}`);
const textoHorario = (t) => `${NOMBRE_DIA[t.dia] || t.dia} ${t.hora} (${t.sede ? `${t.sede} · ` : ''}${t.consultorio})`;

// Pacientes con más de una sesión del mismo área. Con un solo profesional es un
// aviso (por ejemplo, dos sesiones de psicología); con dos profesionales
// distintos del mismo área es un error.
function revisarTratamientos(consultorios) {
  const grupos = new Map();
  consultorios.forEach((c) => c.turnos.forEach((t) => {
    if (!t.disciplina) return;
    t.pacientes.forEach((p) => {
      const clave = `${clavePaciente(p)}|${t.disciplina}`;
      if (!grupos.has(clave)) grupos.set(clave, { paciente: nombreVisible(p), disciplina: t.disciplina, sesiones: [] });
      grupos.get(clave).sesiones.push({ ...t, sede: c.sede, consultorio: c.nombre });
    });
  }));
  const errores = [];
  const repetidos = [];
  grupos.forEach((g) => {
    if (g.sesiones.length < 2) return;
    const porProfesional = new Map();
    g.sesiones.forEach((t) => {
      const nombre = String(t.profesional || '').trim() || 'Sin profesional';
      const clave = normalizar(nombre);
      if (!porProfesional.has(clave)) porProfesional.set(clave, { nombre, sesiones: [] });
      porProfesional.get(clave).sesiones.push(t);
    });
    const item = { ...g, profesionales: [...porProfesional.values()] };
    (item.profesionales.length > 1 ? errores : repetidos).push(item);
  });
  const orden = (a, b) => a.paciente.localeCompare(b.paciente, 'es') || a.disciplina.localeCompare(b.disciplina, 'es');
  return { errores: errores.sort(orden), repetidos: repetidos.sort(orden) };
}

function RevisionTratamientos({ revision }) {
  const { errores, repetidos } = revision;
  if (!errores.length && !repetidos.length) return null;
  const detalle = (item) => item.profesionales
    .map((p) => `${p.nombre}: ${p.sesiones.map(textoHorario).join(', ')}`).join(' · ');
  return (
    <>
      {/* Los errores quedan siempre a la vista, arriba de la agenda; las casillas van marcadas en rojo. */}
      {errores.length > 0 && (
        <div role="alert" className="rounded-xl border-2 border-rose-300 bg-rose-50 p-3 text-sm text-rose-900 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-100 print:hidden">
          <p className="font-bold">
            Error de tratamientos: {errores.length === 1 ? '1 paciente tiene dos profesionales del mismo área' : `${errores.length} pacientes tienen dos profesionales del mismo área`}.
            {' '}Revisá cuál corresponde; las casillas están marcadas en rojo con ⚠.
          </p>
          <ul className="mt-1 space-y-1">
            {errores.map((e) => (
              <li key={`${e.paciente}|${e.disciplina}`}><span className="font-semibold">{e.paciente}</span> · {e.disciplina} · {detalle(e)}</li>
            ))}
          </ul>
        </div>
      )}
      {repetidos.length > 0 && (
        <details className="rounded-xl border border-slate-200 bg-white p-3 text-sm dark:border-slate-700 dark:bg-slate-800 print:hidden">
          <summary className="cursor-pointer font-semibold text-amber-700">
            {repetidos.length === 1 ? '1 paciente con más de una sesión del mismo área' : `${repetidos.length} pacientes con más de una sesión del mismo área`} (mismo profesional)
          </summary>
          <ul className="mt-2 space-y-1 text-slate-600 dark:text-slate-300">
            {repetidos.map((r) => (
              <li key={`${r.paciente}|${r.disciplina}`}>
                <span className="font-semibold">{r.paciente}</span> tiene {r.sesiones.length} sesiones de {r.disciplina} · {detalle(r)}
              </li>
            ))}
          </ul>
        </details>
      )}
    </>
  );
}

// Agenda general de todos los centros juntos. Con más de un centro, cada
// consultorio lleva el nombre de su centro.
async function cargarAgendaCompleta(sedes) {
  const semanas = await Promise.all(sedes.map((s) => obtenerAgendaSemanaApi(s.id)));
  const varios = sedes.length > 1;
  return semanas.flatMap((semana, i) => (semana?.consultorios || [])
    .map((c) => ({ ...c, sedeId: String(sedes[i].id), sede: varios ? sedes[i].nombre : '' })));
}

// Copia de una casilla para poder deshacer un cambio.
const copiaTurno = (t) => ({
  id: t.id, estado: t.estado, profesional: t.profesional || '', disciplina: t.disciplina || '', nota: t.nota || '',
  pacientes: (t.pacientes || []).map((p) => ({ nombre: p.nombre, patientId: p.patientId || null })),
});

// Consultorios agrupados por centro, en el orden en que vienen.
function porCentro(consultorios) {
  const grupos = [];
  consultorios.forEach((c) => {
    const ultimo = grupos[grupos.length - 1];
    if (ultimo && ultimo.sedeId === (c.sedeId || '')) ultimo.consultorios.push(c);
    else grupos.push({ sedeId: c.sedeId || '', sede: c.sede || '', consultorios: [c] });
  });
  return grupos;
}

// Casillas con más de un profesional en el encabezado ("A / B").
const casillasConVariosProfesionales = (consultorios) => consultorios.flatMap((c) => c.turnos
  .filter((t) => (t.profesional || '').split('/').filter((p) => p.trim()).length > 1)
  .map((t) => ({ ...t, consultorio: c.sede ? `${c.sede} · ${c.nombre}` : c.nombre })));

// Horarios que coinciden con los filtros, en una grilla de días por horas.
// Cada casilla muestra el consultorio, el profesional y los pacientes.
function GrillaCoincidencias({ turnos }) {
  const horas = Array.from(new Set(turnos.map((t) => t.hora))).sort();
  const dias = DIAS.filter((d) => turnos.some((t) => t.dia === d.key));
  const celda = (dia, hora) => turnos.filter((t) => t.dia === dia && t.hora === hora);
  return (
    <div className="mt-2 overflow-x-auto">
      <table className="w-full min-w-[640px] table-fixed border-collapse text-xs" aria-label="Horarios que coinciden">
        <thead>
          <tr className="bg-violet-200 text-slate-800 dark:bg-violet-900/60 dark:text-slate-100">
            <th className="w-14 px-1 py-1">Hora</th>
            {dias.map((d) => <th key={d.key} className="px-1 py-1">{d.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {horas.map((hora) => (
            <tr key={hora}>
              <td className="px-1 text-center font-mono font-semibold text-slate-500">{hora}</td>
              {dias.map((d) => (
                <td key={d.key} className="border border-slate-100 p-0.5 align-top dark:border-slate-700">
                  {celda(d.key, hora).map((t) => (
                    <div key={t.id} className={`mb-0.5 rounded px-1 py-0.5 leading-tight ${claseTurno(t)}`}>
                      <span className="block text-[10px] font-bold opacity-80">{t.sede ? `${t.sede} · ` : ''}{t.consultorio}{t.profesional ? ` · ${t.profesional}` : ''}</span>
                      {t.pacientes.length ? t.pacientes.map(nombreVisible).join(' / ') : (ESTADOS[t.estado]?.label || 'Libre')}
                    </div>
                  ))}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Agenda general de todos los centros juntos, filtrable por centro.
// Consultorios por fila en la agenda general; se recuerda en este navegador.
const POR_FILA = [
  { key: 'auto', label: 'Automático', clase: 'grid-cols-[repeat(auto-fill,minmax(min(100%,640px),1fr))]' },
  { key: '1', label: '1 por fila', clase: 'grid-cols-1' },
  { key: '2', label: '2 por fila', clase: 'grid-cols-1 lg:grid-cols-2' },
  { key: '3', label: '3 por fila', clase: 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3' },
];
const leerPreferencia = (clave, inicial) => { try { return window.localStorage.getItem(clave) || inicial; } catch { return inicial; } };
const guardarPreferencia = (clave, valor) => { try { window.localStorage.setItem(clave, valor); } catch { /* sin almacenamiento */ } };

function AgendaSemanal({ sedes, pacientes, editable }) {
  const [porFila, setPorFila] = useState(() => leerPreferencia('cenein.agenda.porFila', 'auto'));
  const elegirPorFila = (valor) => { setPorFila(valor); guardarPreferencia('cenein.agenda.porFila', valor); };
  const [agenda, setAgenda] = useState(null);
  const [profesionales, setProfesionales] = useState([]);
  const [error, setError] = useState('');
  const [editando, setEditando] = useState(null);
  const [encabezado, setEncabezado] = useState(null);
  const [filtroSede, setFiltroSede] = useState('');
  const [filtro, setFiltro] = useState('');
  const [filtroProfesional, setFiltroProfesional] = useState('');
  const [filtroDisciplina, setFiltroDisciplina] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroPaciente, setFiltroPaciente] = useState('');
  // Deshacer con Ctrl+Z: copias de las casillas antes de cada cambio (las últimas 30).
  const deshacer = useRef([]);
  const [pendientesDeshacer, setPendientesDeshacer] = useState(0);
  const [avisoDeshacer, setAvisoDeshacer] = useState('');
  const recordar = (descripcion, turnos) => {
    deshacer.current = [...deshacer.current, { descripcion, turnos: turnos.map(copiaTurno) }].slice(-30);
    setPendientesDeshacer(deshacer.current.length);
    setAvisoDeshacer('');
  };

  const cargar = useCallback(async () => {
    if (!sedes.length) return;
    try {
      const [consultorios, catalogo] = await Promise.all([cargarAgendaCompleta(sedes), obtenerCatalogoProfesionalesApi()]);
      setAgenda({ consultorios });
      setProfesionales(catalogo);
      setError('');
    } catch (err) {
      setError(String(err?.message || 'No se pudo cargar la agenda.'));
    }
  }, [sedes]);
  useEffect(() => { cargar(); }, [cargar]);

  // Ctrl+Z (o Cmd+Z) deshace el último cambio guardado, como en Excel. No actúa
  // mientras se escribe en un campo ni con una ventana de edición abierta.
  const abierto = Boolean(editando || encabezado);
  useEffect(() => {
    if (!editable) return undefined;
    const alTeclear = async (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.shiftKey || e.altKey || String(e.key).toLowerCase() !== 'z') return;
      const destino = e.target;
      if (abierto || destino?.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(destino?.tagName)) return;
      e.preventDefault();
      const ultimo = deshacer.current[deshacer.current.length - 1];
      if (!ultimo) {
        setAvisoDeshacer('No hay cambios para deshacer.');
        return;
      }
      deshacer.current = deshacer.current.slice(0, -1);
      setPendientesDeshacer(deshacer.current.length);
      try {
        await restaurarTurnosAgendaApi(ultimo.turnos);
        setAvisoDeshacer(`Se deshizo el cambio en ${ultimo.descripcion}.`);
        await cargar();
      } catch (err) {
        setAvisoDeshacer(String(err?.message || 'No se pudo deshacer el cambio.'));
      }
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [editable, abierto, cargar]);

  // Con filtros de profesional, disciplina o estado, las casillas que no coinciden
  // se atenúan y se ocultan los consultorios sin ninguna coincidencia.
  const buscado = normalizar(filtroPaciente).trim();
  const hayFiltroCasilla = Boolean(filtroProfesional || filtroDisciplina || filtroEstado || buscado);
  const coincide = useMemo(() => {
    if (!hayFiltroCasilla) return null;
    const estado = FILTROS_ESTADO.find((e) => e.key === filtroEstado);
    // Paciente: busca por apellido o nombre, sin importar mayúsculas ni acentos.
    const tienePaciente = (t) => t.pacientes.some((p) => normalizar(`${p.nombre || ''} ${nombreVisible(p)}`).includes(buscado));
    return (t) => (!filtroProfesional || (filtroProfesional === SIN_PROFESIONAL
      ? !partesProfesional(t.profesional).length
      : partesProfesional(t.profesional).includes(normalizar(filtroProfesional))))
      && (!filtroDisciplina || t.disciplina === filtroDisciplina)
      && (!estado || estado.coincide(t))
      && (!buscado || tienePaciente(t));
  }, [hayFiltroCasilla, filtroProfesional, filtroDisciplina, filtroEstado, buscado]);
  const delCentro = (agenda?.consultorios || []).filter((c) => !filtroSede || c.sedeId === filtroSede);
  const enVista = delCentro.filter((c) => !filtro || String(c.id) === filtro);
  const consultorios = enVista.filter((c) => !coincide || c.turnos.some(coincide));
  const coincidencias = coincide
    ? consultorios.flatMap((c) => c.turnos.filter(coincide).map((t) => ({ ...t, sede: c.sede, consultorio: c.nombre })))
      .sort((a, b) => DIAS.findIndex((d) => d.key === a.dia) - DIAS.findIndex((d) => d.key === b.dia)
        || a.hora.localeCompare(b.hora) || a.consultorio.localeCompare(b.consultorio, 'es'))
    : [];
  const limpiarFiltros = () => {
    setFiltroSede(''); setFiltro(''); setFiltroProfesional(''); setFiltroDisciplina(''); setFiltroEstado(''); setFiltroPaciente('');
  };
  // Conteo del profesional filtrado: pacientes distintos y sesiones en los centros y consultorios elegidos.
  const conteoProfesional = useMemo(() => {
    if (!filtroProfesional || filtroProfesional === SIN_PROFESIONAL) return null;
    const buscado = normalizar(filtroProfesional);
    const suyos = enVista.flatMap((c) => c.turnos.filter((t) => partesProfesional(t.profesional).includes(buscado)));
    // Los pacientes en rojo (no coinciden con ningún paciente del sistema) no se cuentan.
    const vinculados = suyos.flatMap((t) => t.pacientes.filter((p) => p.patientId));
    const enRojo = new Set(suyos.flatMap((t) => t.pacientes.filter((p) => !p.patientId).map(clavePaciente)));
    return {
      pacientes: new Set(vinculados.map(clavePaciente)).size,
      sesiones: vinculados.length,
      enRojo: enRojo.size,
      libres: suyos.filter((t) => t.estado === 'vacio' && !t.pacientes.length).length,
    };
  }, [filtroProfesional, enVista]);
  const revision = useMemo(() => revisarTratamientos(agenda?.consultorios || []), [agenda]);
  const conError = useMemo(() => new Set(revision.errores
    .flatMap((e) => e.profesionales.flatMap((p) => p.sesiones.map((t) => t.id)))), [revision]);
  const conflictos = casillasConVariosProfesionales(agenda?.consultorios || []);
  return (
    <div className="space-y-4">
      <RevisionTratamientos revision={revision} />
      <div className="flex flex-wrap items-end gap-3 print:hidden">
        {sedes.length > 1 && (
          <label className="text-xs font-bold text-slate-600 dark:text-slate-300">
            <span className="mb-1 block">Centro</span>
            <select className={inputClass} aria-label="Filtrar por centro" value={filtroSede}
              onChange={(e) => { setFiltroSede(e.target.value); setFiltro(''); }}>
              <option value="">Todos los centros</option>
              {sedes.map((s) => <option key={s.id} value={String(s.id)}>{s.nombre}</option>)}
            </select>
          </label>
        )}
        <label className="text-xs font-bold text-slate-600 dark:text-slate-300">
          <span className="mb-1 block">Consultorio</span>
          <select className={inputClass} aria-label="Filtrar por consultorio" value={filtro} onChange={(e) => setFiltro(e.target.value)}>
            <option value="">Todos</option>
            {delCentro.map((c) => <option key={c.id} value={String(c.id)}>{c.sede ? `${c.sede} · ` : ''}{c.nombre}</option>)}
          </select>
        </label>
        <label className="text-xs font-bold text-slate-600 dark:text-slate-300">
          <span className="mb-1 block">Profesional</span>
          <select className={inputClass} aria-label="Filtrar por profesional" value={filtroProfesional} onChange={(e) => setFiltroProfesional(e.target.value)}>
            <option value="">Todos</option>
            <option value={SIN_PROFESIONAL}>Sin profesional</option>
            {profesionales.map((p) => <option key={p.id} value={p.nombre}>{p.nombre}</option>)}
          </select>
        </label>
        <label className="text-xs font-bold text-slate-600 dark:text-slate-300">
          <span className="mb-1 block">Disciplina</span>
          <select className={inputClass} aria-label="Filtrar por disciplina" value={filtroDisciplina} onChange={(e) => setFiltroDisciplina(e.target.value)}>
            <option value="">Todas</option>
            {[...DISCIPLINAS, DISCIPLINA_AGRUPADA].map((d) => <option key={d.key} value={d.key}>{d.key === DISCIPLINA_AGRUPADA.key ? `${d.key} (sin separar)` : d.key}</option>)}
          </select>
        </label>
        <label className="text-xs font-bold text-slate-600 dark:text-slate-300">
          <span className="mb-1 block">Estado</span>
          <select className={inputClass} aria-label="Filtrar por estado" value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
            <option value="">Todos</option>
            {FILTROS_ESTADO.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
          </select>
        </label>
        <label className="text-xs font-bold text-slate-600 dark:text-slate-300">
          <span className="mb-1 block">Paciente</span>
          <input type="search" className={inputClass} aria-label="Buscar paciente por apellido" placeholder="Apellido"
            value={filtroPaciente} onChange={(e) => setFiltroPaciente(e.target.value)} autoComplete="off" />
        </label>
        {(filtroSede || filtro || hayFiltroCasilla) && (
          <button type="button" className={botonSecundario} onClick={limpiarFiltros}>Limpiar filtros</button>
        )}
        <button type="button" className={`${botonSecundario} ml-auto`} onClick={imprimir}>
          <span className="material-symbols-outlined text-base">print</span>Imprimir
        </button>
      </div>
      {conteoProfesional && (
        <p className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-100 print:hidden" role="status">
          <span className="font-bold">{filtroProfesional}</span> atiende{' '}
          <span className="font-bold">{conteoProfesional.pacientes === 1 ? '1 paciente' : `${conteoProfesional.pacientes} pacientes`}</span>
          {' '}en {conteoProfesional.sesiones === 1 ? '1 sesión semanal' : `${conteoProfesional.sesiones} sesiones semanales`}
          {conteoProfesional.libres > 0 && ` · ${conteoProfesional.libres === 1 ? '1 turno libre' : `${conteoProfesional.libres} turnos libres`}`}
          {(filtroSede || filtro) ? ' (en el centro o consultorio elegido).' : '.'}
          {conteoProfesional.enRojo > 0 && (
            <span className="block text-rose-700 dark:text-rose-300">
              {conteoProfesional.enRojo === 1 ? 'No se cuenta 1 paciente en rojo' : `No se cuentan ${conteoProfesional.enRojo} pacientes en rojo`} (no coinciden con un paciente del sistema).
            </span>
          )}
        </p>
      )}
      {coincide && agenda && (
        <details className="rounded-xl border border-slate-200 bg-white p-3 text-sm dark:border-slate-700 dark:bg-slate-800 print:hidden" open={coincidencias.length <= 30}>
          <summary className="cursor-pointer font-semibold text-slate-800 dark:text-slate-100">
            {coincidencias.length === 1 ? '1 horario coincide' : `${coincidencias.length} horarios coinciden`} con los filtros
          </summary>
          {coincidencias.length > 0 && <GrillaCoincidencias turnos={coincidencias} />}
        </details>
      )}
      <Leyenda />
      {conflictos.length > 0 && (
        <div role="alert" className="rounded-xl border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200">
          <p className="font-bold">Hay casillas con más de un profesional.</p>
          <p>Cada casilla debe tener un solo profesional. Corregí estos encabezados antes de aprobar sus agendas:</p>
          <ul>{conflictos.map((t) => <li key={t.id}>{t.consultorio} · {NOMBRE_DIA[t.dia] || t.dia} {t.hora} · {t.profesional}</li>)}</ul>
        </div>
      )}
      {editable && (
        <p className="text-xs text-slate-500 print:hidden">
          Tocá un encabezado de profesional para cambiar el nombre o el color. Tocá una hora para agregar un encabezado nuevo desde ahí.
          {' '}Ctrl+Z deshace el último cambio{pendientesDeshacer ? ` (${pendientesDeshacer} para deshacer)` : ''}.
        </p>
      )}
      {avisoDeshacer && (
        <p role="status" className="rounded-xl bg-slate-800 px-3 py-2 text-sm font-semibold text-white print:hidden">{avisoDeshacer}</p>
      )}
      {error && <p className="text-sm font-semibold text-rose-600">{error}</p>}
      <div className="flex flex-wrap items-center gap-1 text-xs print:hidden" role="group" aria-label="Consultorios por fila">
        <span className="mr-1 font-bold text-slate-600 dark:text-slate-300">Consultorios por fila:</span>
        {POR_FILA.map((o) => (
          <button key={o.key} type="button" aria-pressed={porFila === o.key} onClick={() => elegirPorFila(o.key)}
            className={`rounded-full px-3 py-1 font-bold ${porFila === o.key ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200'}`}>
            {o.key === 'auto' ? o.label : o.key}
          </button>
        ))}
      </div>
      {/* Cada centro va separado por una línea negra. Los consultorios ocupan
          tantas columnas como entren: al alejar la pantalla (Ctrl y ruedita) entran más. */}
      <div className="agenda-print-area space-y-6">
        {porCentro(consultorios).map((grupo) => (
          <section key={grupo.sedeId} aria-label={grupo.sede ? `Centro ${grupo.sede}` : undefined}
            className={grupo.sede ? 'border-t-4 border-slate-900 pt-3 dark:border-slate-100' : ''}>
            {grupo.sede && <h2 className="mb-3 text-lg font-extrabold uppercase tracking-wide text-slate-900 dark:text-white">{grupo.sede}</h2>}
            <div className={`grid ${porFila === '3' ? 'gap-2' : 'gap-4'} ${(POR_FILA.find((o) => o.key === porFila) || POR_FILA[0]).clase}`}>
              {grupo.consultorios.map((c) => (
                <GrillaConsultorio key={c.id} consultorio={c} editable={editable} coincide={coincide} conError={conError} chica={porFila === '3'}
                  alEditar={(turno) => setEditando({ turno, consultorio: c.nombre })}
                  alEditarProfesional={(inicial, horas) => setEncabezado({ consultorio: c, inicial, horas })} />
              ))}
            </div>
          </section>
        ))}
      </div>
      {encabezado && (
        <EditorProfesional
          consultorio={encabezado.consultorio.nombre}
          inicial={encabezado.inicial}
          horas={encabezado.horas}
          profesionales={profesionales}
          alCerrar={() => setEncabezado(null)}
          alGuardar={async (datos) => {
            const { consultorio } = encabezado;
            const afectados = consultorio.turnos.filter((t) => {
              if (!datos.dias.includes(t.dia) || t.hora < datos.desde) return false;
              const hasta = datos.hasta === FIN_TRAMO ? finTramo(consultorio.turnos, t.dia, datos.desde) : datos.hasta;
              return Boolean(hasta) && t.hora <= hasta;
            });
            if (datos.hasta !== FIN_TRAMO) {
              await guardarProfesionalAgendaApi(consultorio.id, datos);
            } else {
              // Cada día termina donde cambia su propio profesional.
              const porHasta = new Map();
              datos.dias.forEach((dia) => {
                const hasta = finTramo(consultorio.turnos, dia, datos.desde);
                if (hasta) porHasta.set(hasta, [...(porHasta.get(hasta) || []), dia]);
              });
              for (const [hasta, dias] of porHasta) {
                await guardarProfesionalAgendaApi(consultorio.id, { ...datos, dias, hasta });
              }
            }
            if (afectados.length) recordar(`el encabezado de ${consultorio.nombre}`, afectados);
            setEncabezado(null);
            await cargar();
          }}
        />
      )}
      {editando && (
        <EditorTurno
          turno={editando.turno}
          consultorio={editando.consultorio}
          pacientes={pacientes}
          profesionales={profesionales}
          alCerrar={() => setEditando(null)}
          alGuardar={async (datos) => {
            await guardarTurnoAgendaApi(editando.turno.id, datos);
            recordar(`${editando.consultorio} · ${NOMBRE_DIA[editando.turno.dia] || editando.turno.dia} ${editando.turno.hora}`, [editando.turno]);
            setEditando(null);
            await cargar();
          }}
        />
      )}
    </div>
  );
}

const ESTADOS_ASISTENCIA = [
  { key: 'presente', label: 'P', titulo: 'Presente', activo: 'bg-emerald-600 text-white' },
  { key: 'ausente', label: 'A', titulo: 'Ausente', activo: 'bg-rose-600 text-white' },
  { key: 'justificado', label: 'J', titulo: 'Ausente con aviso', activo: 'bg-amber-500 text-white' },
];

function PlanillaDia({ sedeId }) {
  const [fecha, setFecha] = useState(fechaLocalIso());
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');

  const cargar = useCallback(async () => {
    if (!sedeId) return;
    try {
      setDatos(await obtenerAgendaDiaApi(sedeId, fecha));
      setError('');
    } catch (err) {
      setError(String(err?.message || 'No se pudo cargar la planilla.'));
    }
  }, [sedeId, fecha]);
  useEffect(() => { cargar(); }, [cargar]);

  const estados = useMemo(
    () => new Map((datos?.asistencias || []).map((a) => [`${a.consultorio}|${a.hora}|${a.nombre}`, a.estado])),
    [datos]
  );
  const marcar = async (fila, estado) => {
    const nombre = nombreVisible(fila.paciente);
    const actual = estados.get(`${fila.consultorio}|${fila.turno.hora}|${nombre}`);
    try {
      await guardarAsistenciasAgendaApi(sedeId, fecha, [{
        consultorio: fila.consultorio,
        hora: fila.turno.hora,
        nombre,
        patientId: fila.paciente.patientId,
        profesional: fila.turno.profesional,
        estado: actual === estado ? null : estado,
      }]);
      await cargar();
    } catch (err) {
      setError(String(err?.message || 'No se pudo guardar la asistencia.'));
    }
  };

  const porConsultorio = useMemo(() => {
    const grupos = new Map();
    filasPlanillaDia(datos?.consultorios).forEach((fila) => {
      if (!grupos.has(fila.consultorio)) grupos.set(fila.consultorio, []);
      grupos.get(fila.consultorio).push(fila);
    });
    return Array.from(grupos.entries());
  }, [datos]);
  const total = porConsultorio.reduce((acc, [, filas]) => acc + filas.length, 0);
  const presentes = porConsultorio.reduce((acc, [consultorio, filas]) => acc + filas.filter((fila) =>
    estados.get(`${consultorio}|${fila.turno.hora}|${nombreVisible(fila.paciente)}`) === 'presente'
  ).length, 0);

  return (
    <div className="space-y-4">
      <div className={`${tarjeta} flex flex-wrap items-end gap-3 p-4 print:hidden`}>
        <button type="button" className={botonSecundario} aria-label="Día anterior" onClick={() => setFecha(sumarDias(fecha, -1))}>
          <span className="material-symbols-outlined text-base">chevron_left</span>
        </button>
        <label className="text-xs font-bold text-slate-600 dark:text-slate-300">
          <span className="mb-1 block">Fecha</span>
          <InputFecha className={inputClass} value={fecha} onChange={(e) => e.target.value && setFecha(e.target.value)} />
        </label>
        <button type="button" className={botonSecundario} aria-label="Día siguiente" onClick={() => setFecha(sumarDias(fecha, 1))}>
          <span className="material-symbols-outlined text-base">chevron_right</span>
        </button>
        <button type="button" className={botonSecundario} onClick={() => setFecha(fechaLocalIso())}>Hoy</button>
        <div className="ml-auto flex gap-2">
          <button type="button" className={botonSecundario} disabled={!datos}
            onClick={() => descargar(planillaDiaCsv({ sede: datos.sede.nombre, fecha, consultorios: datos.consultorios, asistencias: datos.asistencias }),
              `agenda-${datos.sede.nombre}-${fecha}.csv`)}>
            <span className="material-symbols-outlined text-base">download</span>Descargar
          </button>
          <button type="button" className={botonSecundario} onClick={imprimir}>
            <span className="material-symbols-outlined text-base">print</span>Imprimir
          </button>
        </div>
      </div>
      {error && <p className="text-sm font-semibold text-rose-600">{error}</p>}
      <div className="agenda-print-area space-y-4">
        <h3 className="text-xl font-extrabold text-slate-900 dark:text-white">
          {datos?.sede?.nombre} · {formatearFecha(fecha)} · {total} {total === 1 ? 'turno' : 'turnos'}
          <span className="ml-2 text-sm font-semibold text-slate-500 print:hidden">{presentes} presentes</span>
        </h3>
        {!total && datos && (
          <div className={`${tarjeta} p-10 text-center text-sm text-slate-500`}>No hay pacientes agendados para este día.</div>
        )}
        {/* Un cuadrante por consultorio, como la agenda general: hora, profesional
            y pacientes. Entran varios por fila y en la impresión, tres por hoja. */}
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3 print:grid-cols-3 print:gap-1.5">
          {porConsultorio.map(([consultorio, filas]) => {
            const porHora = [];
            filas.forEach((fila) => {
              if (porHora.at(-1)?.hora !== fila.turno.hora) porHora.push({ hora: fila.turno.hora, profesional: fila.turno.profesional, filas: [] });
              porHora.at(-1).filas.push(fila);
            });
            return (
              <section key={consultorio} aria-label={consultorio} className={`${tarjeta} overflow-hidden agenda-grupo print:rounded-none print:shadow-none`}>
                <header className="border-b border-slate-100 bg-violet-200 px-3 py-1 dark:border-slate-700 dark:bg-violet-900/60">
                  <h4 className="text-sm font-bold text-slate-900 dark:text-white">{consultorio}</h4>
                </header>
                {/* El profesional va como encabezado cuando cambia (como en la agenda
                    general), así el nombre del paciente tiene todo el ancho. */}
                <table className="w-full table-fixed border-collapse text-left text-xs print:text-[9px]">
                  <colgroup><col className="w-12 print:w-9" /><col /></colgroup>
                  <tbody>
                    {porHora.map((h, i) => (
                      <React.Fragment key={h.hora}>
                        {(i === 0 || porHora[i - 1].profesional !== h.profesional) && (
                          <tr className="border-t border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/40">
                            <th colSpan={2} scope="colgroup" className="px-2 py-0.5 text-left font-bold text-slate-700 dark:text-slate-200">{h.profesional || 'Sin profesional'}</th>
                          </tr>
                        )}
                      <tr className="border-t border-slate-100 align-top dark:border-slate-700">
                        <td className="px-1 py-1 font-mono font-semibold text-slate-500">{h.hora.slice(0, 5)}</td>
                        <td className="px-1 py-0.5">
                          {h.filas.map((fila) => {
                            const nombre = nombreVisible(fila.paciente);
                            const actual = estados.get(`${fila.consultorio}|${fila.turno.hora}|${nombre}`);
                            return (
                              <div key={`${fila.turno.id}-${fila.paciente.id}`} className="flex items-center justify-between gap-1 py-0.5">
                                <span className="min-w-0 leading-tight">
                                  <span className="font-semibold text-slate-900 dark:text-white">{nombre}</span>
                                  {!fila.paciente.patientId && (
                                    <span className={`ml-1 rounded px-1 py-0.5 text-[10px] font-bold ${NO_ENCONTRADO.clase}`}>No cargado</span>
                                  )}
                                </span>
                                <span className="hidden shrink-0 print:inline">{ESTADOS_ASISTENCIA.find((e) => e.key === actual)?.label || '☐P ☐A'}</span>
                                <span className="inline-flex shrink-0 gap-0.5 print:hidden">
                                  {ESTADOS_ASISTENCIA.map((e) => (
                                    <button key={e.key} type="button" title={e.titulo} aria-label={`${e.titulo}: ${nombre}`} aria-pressed={actual === e.key}
                                      onClick={() => marcar(fila, e.key)}
                                      className={`h-6 w-6 rounded text-[11px] font-bold ${actual === e.key ? e.activo : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-200'}`}>
                                      {e.label}
                                    </button>
                                  ))}
                                </span>
                              </div>
                            );
                          })}
                        </td>
                      </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function NoEncontrados({ pacientes, editable, alContar }) {
  const [grupos, setGrupos] = useState([]);
  const [eleccion, setEleccion] = useState({});
  const [mensaje, setMensaje] = useState('');
  const cargar = useCallback(async () => {
    try {
      const lista = await obtenerSinAsociarAgendaApi();
      setGrupos(lista);
      alContar?.(lista.length);
    } catch (err) {
      setMensaje(String(err?.message || 'No se pudo cargar el listado.'));
    }
  }, [alContar]);
  useEffect(() => { cargar(); }, [cargar]);
  const paginado = usePaginado(grupos);

  const asociar = async (grupo) => {
    try {
      const resultado = await asociarPacienteAgendaApi(grupo.ids, eleccion[grupo.nombre]);
      const cantidad = resultado?.asociados ?? grupo.ids.length;
      setMensaje(`${grupo.nombre}: asociado en ${cantidad} ${cantidad === 1 ? 'turno' : 'turnos'}.`);
      await cargar();
    } catch (err) {
      setMensaje(String(err?.message || 'No se pudo asociar.'));
      // Si la agenda cambió mientras tanto, se recarga la lista.
      await cargar();
    }
  };

  return (
    <section className={`${tarjeta} overflow-hidden`}>
      <header className="border-b border-slate-100 px-5 py-3 dark:border-slate-700">
        <h3 className="font-bold text-slate-900 dark:text-white">
          Pacientes de la agenda que no están cargados en el sistema
          {grupos.length > 0 && <span className="ml-2 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-bold text-rose-800">{grupos.length}</span>}
        </h3>
        <p className="mt-1 text-xs text-slate-500">
          Sus casillas se ven en <span className={`rounded px-1 font-bold ${NO_ENCONTRADO.clase}`}>rojo claro</span>.
          Asocialos con el paciente correcto o cargalos por Admisión y después asocialos acá. Al asociar, se actualizan todos sus turnos.
        </p>
      </header>
      {mensaje && <p className="px-5 pt-3 text-sm font-semibold text-emerald-700">{mensaje}</p>}
      <ul className="divide-y divide-slate-100 dark:divide-slate-700">
        {paginado.enPagina.map((g) => (
          <li key={g.nombre} className="grid gap-3 px-5 py-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-start">
            <div>
              <p className="font-semibold text-slate-900 dark:text-white">{g.nombre}</p>
              <p className="text-xs text-slate-500">
                {g.turnos.map((t) => `${t.sede} · ${t.consultorio} · ${t.dia} ${t.hora}`).join(' — ')}
              </p>
            </div>
            {editable && (
              <>
                <BuscadorPaciente pacientes={pacientes} valor={eleccion[g.nombre] || null} etiqueta={`Paciente para ${g.nombre}`}
                  alCambiar={(id) => setEleccion({ ...eleccion, [g.nombre]: id })} />
                <button type="button" className={botonPrimario} disabled={!eleccion[g.nombre]} onClick={() => asociar(g)}>Asociar</button>
              </>
            )}
          </li>
        ))}
        {!grupos.length && <li className="px-5 py-8 text-center text-sm text-slate-500">Todos los nombres de la agenda están asociados a un paciente.</li>}
      </ul>
      <div className="px-5 pb-3"><Paginador {...paginado} total={grupos.length} etiqueta="pacientes no encontrados" /></div>
    </section>
  );
}

const mesActual = () => fechaLocalIso().slice(0, 7);

// Selector de mes en español (el input month nativo sigue el idioma del navegador).
const MESES_NOMBRE = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
function InputMes({ value, onChange }) {
  const [anio, mes] = String(value || mesActual()).split('-');
  const actual = Number(mesActual().slice(0, 4));
  const anios = Array.from(new Set([actual - 1, actual, actual + 1, Number(anio)])).sort();
  return (
    <span className="inline-flex gap-1">
      <select className={inputClass} aria-label="Mes" value={mes} onChange={(e) => onChange(`${anio}-${e.target.value}`)}>
        {MESES_NOMBRE.map((nombre, i) => <option key={nombre} value={String(i + 1).padStart(2, '0')}>{nombre}</option>)}
      </select>
      <select className={inputClass} aria-label="Año" value={anio} onChange={(e) => onChange(`${e.target.value}-${mes}`)}>
        {anios.map((a) => <option key={a} value={String(a)}>{a}</option>)}
      </select>
    </span>
  );
}

const ESTADO_PLANILLA = {
  pendiente: { texto: 'Pendiente de aprobación', clase: 'bg-amber-100 text-amber-800' },
  aprobada: { texto: 'Aprobada', clase: 'bg-emerald-100 text-emerald-800' },
  rechazada: { texto: 'Rechazada', clase: 'bg-rose-100 text-rose-800' },
  sin_cambios: { texto: 'Igual a la general', clase: 'bg-slate-100 text-slate-700' },
};
// Cuadradito de color de cada estado en las tarjetas chicas.
const CUADRADO_ESTADO = { pendiente: 'bg-amber-400', aprobada: 'bg-emerald-500', rechazada: 'bg-rose-500', sin_cambios: 'bg-slate-400' };
const ACCION_HISTORIAL = { cargada: 'Cargada', actualizada: 'Actualizada', aprobada: 'Aprobada', rechazada: 'Rechazada' };
const NOMBRE_DIA = Object.fromEntries(DIAS.map((d) => [d.key, d.label]));
const OMITIR_HORARIO = 'omitir';

const textoCasilla = (c) => {
  if (!c) return '—';
  if (c.pacientes?.length) return c.pacientes.join(' / ');
  return ESTADOS[c.estado]?.label || c.estado;
};
const fechaHora = (valor) => (valor ? new Date(valor).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) : '');
const claveHorario = (d) => `${d.dia}|${d.hora}`;

// Diferencias por día y hora entre lo que informó el profesional y la agenda
// general, explicadas para cualquier usuario. Los horarios que no están en la
// agenda no tienen consultorio: el administrador elige uno libre a esa hora o
// los deja fuera de la agenda.
const TIPO_DIFERENCIA = {
  cambio: 'Cambian los pacientes',
  nuevo: 'Horario que no está en la agenda',
  quitado: 'Deja de atender en este horario',
  conflicto: 'Casilla con más de un profesional',
};
const nombresDe = (c) => (c?.pacientes || []).map((n) => String(n).trim()).filter(Boolean);
const mismoNombre = (a, b) => normalizar(a) === normalizar(b);

// Qué cambió en una casilla, en palabras.
function explicarDiferencia(d) {
  if (d.tipo === 'nuevo') return 'No tiene consultorio a esa hora: elegí uno.';
  if (d.tipo === 'quitado') return 'En la agenda general tiene este horario y en su respuesta lo dejó vacío. Al aprobar, la casilla queda libre.';
  if (d.tipo === 'conflicto') return 'La casilla tiene más de un profesional. Corregí el encabezado en la agenda general.';
  const antes = nombresDe(d.antes);
  const despues = nombresDe(d.despues);
  const agrega = despues.filter((n) => !antes.some((m) => mismoNombre(m, n)));
  const quita = antes.filter((n) => !despues.some((m) => mismoNombre(m, n)));
  const partes = [];
  if (agrega.length) partes.push(`Agrega: ${agrega.join(', ')}.`);
  if (quita.length) partes.push(`Ya no atiende: ${quita.join(', ')}.`);
  if (!partes.length) partes.push(`Cambia de "${textoCasilla(d.antes)}" a "${textoCasilla(d.despues)}".`);
  return partes.join(' ');
}

const recortar = (texto, largo) => (String(texto).length > largo ? `${String(texto).slice(0, largo - 1)}…` : String(texto));

function DiferenciasPlanilla({ diferencias, asignaciones, alAsignar }) {
  if (!diferencias.length) return <p className="text-sm text-slate-500">Lo que informó coincide con la agenda general: no hay nada para revisar.</p>;
  return (
    <div className="space-y-2">
      <p className="text-sm text-slate-600 dark:text-slate-300">
        Estos horarios son distintos de lo que figura hoy en la agenda general. Revisalos y, si están bien, tocá Aprobar para pasarlos a la agenda.
      </p>
      <div className="overflow-x-auto">
        {/* Anchos fijos: el selector de consultorio no puede aplastar las demás columnas. */}
        <table className="w-full min-w-[56rem] table-fixed text-left text-sm">
          <colgroup>
            <col className="w-28" />
            <col className="w-56" />
            <col className="w-48" />
            <col className="w-56" />
            <col className="w-80" />
          </colgroup>
          <thead className="text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-1">Día y hora</th>
              <th className="px-3 py-1">Qué cambió</th>
              <th className="px-3 py-1">Hoy en la agenda general</th>
              <th className="px-3 py-1">Lo que informó el profesional</th>
              <th className="px-3 py-1">Consultorio</th>
            </tr>
          </thead>
          <tbody>
            {diferencias.map((d) => (
              <tr key={`${d.tipo}-${d.turnoId || ''}-${claveHorario(d)}`} className="border-t border-slate-100 align-top dark:border-slate-700">
                <td className="whitespace-nowrap px-3 py-2">{NOMBRE_DIA[d.dia] || d.dia} {d.hora}</td>
                <td className="px-3 py-2">
                  <span className="font-semibold">{TIPO_DIFERENCIA[d.tipo] || d.tipo}</span>
                  <span className="block text-xs text-slate-500">{explicarDiferencia(d)}</span>
                </td>
                <td className="break-words px-3 py-2 text-slate-500">{d.tipo === 'nuevo' ? 'No tiene este horario' : textoCasilla(d.antes)}</td>
                <td className="break-words px-3 py-2">{d.tipo === 'quitado' ? 'Lo dejó vacío' : textoCasilla(d.despues)}</td>
                <td className="px-3 py-2">
                  {d.tipo !== 'nuevo' && (
                    <>
                      {d.sede} · {d.consultorio}
                      <span className="block text-xs text-slate-500">El que ya tiene asignado.</span>
                      {d.tipo === 'conflicto' && <span className="block text-xs text-rose-700">{d.profesional}: corregí el encabezado en la agenda general.</span>}
                    </>
                  )}
                  {d.tipo === 'nuevo' && alAsignar && (
                    <select className={`${inputClass} w-full`} aria-label={`Consultorio para ${NOMBRE_DIA[d.dia] || d.dia} ${d.hora}`}
                      value={asignaciones[claveHorario(d)] ?? ''} onChange={(e) => alAsignar(claveHorario(d), e.target.value)}>
                      <option value="">Elegir un consultorio…</option>
                      <optgroup label="Libres">
                        {d.opciones.filter((o) => !o.ocupada).map((o) => (
                          <option key={o.turnoId} value={String(o.turnoId)}>
                            {o.sede} · {o.consultorio}{o.profesional ? ` (casilla de ${o.profesional})` : ''}
                          </option>
                        ))}
                      </optgroup>
                      {d.opciones.some((o) => o.ocupada) && (
                        <optgroup label="Ocupados a esa hora (se reemplaza lo que tienen)">
                          {d.opciones.filter((o) => o.ocupada).map((o) => (
                            <option key={o.turnoId} value={String(o.turnoId)}>
                              {o.sede} · {o.consultorio} · {o.profesional || 'sin profesional'}: {recortar(textoCasilla(o.antes), 30)}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      <option value={OMITIR_HORARIO}>No pasar a la agenda</option>
                    </select>
                  )}
                  {d.tipo === 'nuevo' && !alAsignar && <span className="text-slate-500">Lo asigna la administración</span>}
                  {d.tipo === 'nuevo' && alAsignar && !d.opciones.length && (
                    <span className="block text-xs text-amber-700">No hay consultorios con esa hora en la agenda.</span>
                  )}
                  {d.tipo === 'nuevo' && alAsignar && d.opciones.find((o) => o.ocupada && String(o.turnoId) === String(asignaciones[claveHorario(d)])) && (
                    <span className="block text-xs font-semibold text-rose-700">Ese consultorio está ocupado: al aprobar, se reemplaza lo que tiene.</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Horas de las grillas de planilla: siempre de 08 a 18.
const HORAS_PLANILLA = Array.from({ length: 11 }, (_, i) => `${String(8 + i).padStart(2, '0')}:00`);

// La planilla que entregó el profesional, como grilla de días y horas, con sus
// pacientes. Las casillas con diferencias con la agenda general van resaltadas.
// Con alEditar (administración), cada casilla se toca para corregir sus pacientes.
function GrillaPlanilla({ turnos, diferencias, alEditar }) {
  const conDatos = turnos.filter((t) => t.pacientes.length || !['vacio', 'sin_uso'].includes(t.estado));
  if (!conDatos.length && !alEditar) return <p className="text-sm text-slate-500">La planilla no tiene horarios cargados.</p>;
  const horas = [...new Set([...HORAS_PLANILLA, ...conDatos.map((t) => t.hora)])].sort();
  const casilla = new Map(conDatos.map((t) => [claveHorario(t), t]));
  const cambiadas = new Set(diferencias.map(claveHorario));
  const pacientes = new Map();
  conDatos.forEach((t) => t.pacientes.forEach((nombre) => {
    if (!pacientes.has(nombre)) pacientes.set(nombre, []);
    pacientes.get(nombre).push(`${NOMBRE_DIA[t.dia] || t.dia} ${t.hora}`);
  }));
  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] table-fixed border-collapse text-xs" aria-label="Planilla entregada">
          <thead>
            <tr>
              <th className="w-16 border border-slate-200 bg-slate-50 px-2 py-1 text-left dark:border-slate-700 dark:bg-slate-800">Hora</th>
              {DIAS.map((d) => (
                <th key={d.key} className="border border-slate-200 bg-slate-50 px-2 py-1 text-left dark:border-slate-700 dark:bg-slate-800">{d.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {horas.map((hora) => (
              <tr key={hora}>
                <td className="border border-slate-200 px-2 py-1 font-semibold dark:border-slate-700">{hora}</td>
                {DIAS.map((d) => {
                  const t = casilla.get(`${d.key}|${hora}`);
                  const cambio = cambiadas.has(`${d.key}|${hora}`);
                  const clase = t ? (t.pacientes.length ? ESTADOS.ocupado.clase : ESTADOS[t.estado]?.clase || '') : '';
                  return (
                    <td key={d.key} className={`break-words border border-slate-200 px-2 py-1 align-top dark:border-slate-700 ${clase} ${cambio ? 'ring-2 ring-inset ring-amber-500' : ''}`}
                      title={cambio ? 'Distinto de la agenda general' : undefined}>
                      {alEditar ? (
                        <button type="button" className="block min-h-[1.5rem] w-full text-left hover:brightness-95"
                          aria-label={`Editar ${d.label} ${hora}`} title="Tocá para corregir los pacientes"
                          onClick={() => alEditar(`${d.key}|${hora}`)}>
                          {t ? textoCasilla(t) : ''}
                        </button>
                      ) : (t ? textoCasilla(t) : '')}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {cambiadas.size > 0 && <p className="text-xs text-amber-700">Las casillas con borde naranja son distintas de la agenda general.</p>}
      {alEditar && <p className="text-xs text-slate-500">Tocá una casilla para corregir sus pacientes.</p>}
      <div>
        <h4 className="text-xs font-bold uppercase text-slate-500">Pacientes ({pacientes.size})</h4>
        <ul className="mt-1 grid gap-1 text-sm sm:grid-cols-2">
          {[...pacientes.entries()].sort((a, b) => a[0].localeCompare(b[0], 'es')).map(([nombre, horarios]) => (
            <li key={nombre}><span className="font-semibold">{nombre}</span> <span className="text-slate-500">· {horarios.join(', ')}</span></li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// Edición de la planilla de un profesional: en cada día y hora, sus pacientes
// separados por "/". Al guardar queda una versión nueva y, si no hay horarios
// nuevos que necesiten consultorio, pasa directo a la agenda general.
const separarNombres = (texto) => String(texto || '').split('/').map((n) => n.trim()).filter(Boolean);

function EditorPlanilla({ planilla, turnos, alTerminar, alCancelar, enfocar, resaltar = '' }) {
  const { pacientes } = usePacientes();
  const inicial = useMemo(() => Object.fromEntries(turnos.map((t) => [claveHorario(t), t.pacientes.join(' / ')])), [turnos]);
  const [celdas, setCeldas] = useState(inicial);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const nombres = useMemo(() => [...new Set((pacientes || []).filter((p) => !p.dadoDeBaja)
    .map((p) => [p.apellido, p.nombre].filter(Boolean).join(' ')).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'es')), [pacientes]);
  const cambiadas = Object.keys(celdas).filter((k) => separarNombres(celdas[k]).join('|') !== separarNombres(inicial[k]).join('|'));
  const guardar = async () => {
    setError('');
    setGuardando(true);
    try {
      const enviados = cambiadas.map((k) => {
        const [dia, hora] = k.split('|');
        return { dia, hora, pacientes: separarNombres(celdas[k]) };
      });
      const r = await editarPlanillaProfesionalApi(planilla.id, enviados);
      let mensaje = 'No había cambios para guardar.';
      if (!r.sinCambios && r.estado === 'sin_cambios') mensaje = 'Guardado: la planilla quedó igual a la agenda general.';
      else if (!r.sinCambios && r.estado === 'pendiente' && !r.nuevos && !r.conflictos) {
        await aprobarPlanillaProfesionalApi(planilla.id, {});
        mensaje = 'Guardado y pasado a la agenda general.';
      } else if (!r.sinCambios && r.conflictos) {
        mensaje = 'Guardado. No se pasó a la agenda general: hay casillas con más de un profesional.';
      } else if (!r.sinCambios) {
        mensaje = `Guardado. ${r.nuevos === 1 ? 'Hay 1 horario nuevo' : `Hay ${r.nuevos} horarios nuevos`}: elegí el consultorio en "Revisar cambios" y aprobá para pasarlo a la agenda general.`;
      }
      await alTerminar(mensaje);
    } catch (err) {
      setError(String(err?.message || 'No se pudo guardar la planilla.'));
    } finally {
      setGuardando(false);
    }
  };
  return (
    <div className="space-y-3" aria-label="Editar planilla">
      <p className="text-xs text-slate-600 dark:text-slate-300">
        Escribí los pacientes de cada horario separados por "/". Al guardar, si no hay horarios nuevos, los cambios pasan a la agenda general.
      </p>
      <datalist id={`pacientes-planilla-${planilla.id}`}>
        {nombres.map((n) => <option key={n} value={n} />)}
      </datalist>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] table-fixed border-collapse text-xs">
          <thead>
            <tr>
              <th className="w-16 border border-slate-200 bg-slate-50 px-2 py-1 text-left dark:border-slate-700 dark:bg-slate-800">Hora</th>
              {DIAS.map((d) => <th key={d.key} className="border border-slate-200 bg-slate-50 px-2 py-1 text-left dark:border-slate-700 dark:bg-slate-800">{d.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {HORAS_PLANILLA.map((hora) => (
              <tr key={hora}>
                <td className="border border-slate-200 px-2 py-1 font-semibold dark:border-slate-700">{hora}</td>
                {DIAS.map((d) => {
                  const k = `${d.key}|${hora}`;
                  const marcada = resaltar && separarNombres(celdas[k]).some((n) => normalizar(n) === normalizar(resaltar));
                  const fondo = cambiadas.includes(k) ? 'bg-amber-50 dark:bg-amber-950' : marcada ? 'bg-green-200 dark:bg-green-900' : '';
                  return (
                    <td key={d.key} className={`border border-slate-200 p-0.5 dark:border-slate-700 ${fondo}`}>
                      <input className="w-full rounded border border-transparent bg-transparent px-1 py-1 text-xs focus:border-emerald-500 focus:outline-none"
                        list={`pacientes-planilla-${planilla.id}`} aria-label={`${d.label} ${hora}`} autoFocus={enfocar === k}
                        value={celdas[k] ?? ''} onChange={(e) => setCeldas((c) => ({ ...c, [k]: e.target.value }))} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {error && <p role="alert" className="text-sm font-semibold text-rose-600">{error}</p>}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={botonPrimario} disabled={guardando || !cambiadas.length} onClick={guardar}>
          {guardando ? 'Guardando…' : 'Guardar y pasar a la agenda general'}
        </button>
        <button type="button" className={botonSecundario} disabled={guardando} onClick={alCancelar}>Cancelar</button>
        <span className="text-xs text-slate-500">{cambiadas.length ? `${cambiadas.length} casilla${cambiadas.length === 1 ? '' : 's'} modificada${cambiadas.length === 1 ? '' : 's'}` : 'Sin cambios'}</span>
      </div>
    </div>
  );
}

// Pacientes que el profesional informó y que ya tienen el mismo área con otro
// profesional en la agenda general. Se avisa antes de aprobar, sin bloquear.
const textoTratamiento = (t) => `${t.paciente} ya tiene ${t.disciplina} con ${t.otros
  .map((o) => `${o.profesional || 'otro profesional'} el ${textoHorario(o)}`).join(', ')}`;

function AvisoTratamientos({ tratamientos }) {
  if (!tratamientos.length) return null;
  return (
    <div role="alert" className="rounded-lg border-2 border-rose-300 bg-rose-50 p-3 text-sm text-rose-900 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-100">
      <p className="font-bold">
        Error de tratamientos: {tratamientos.length === 1 ? '1 paciente ya tiene' : `${tratamientos.length} pacientes ya tienen`} el mismo área con otro profesional.
        {' '}Si aprobás, quedaría con dos profesionales del mismo área.
      </p>
      <ul className="mt-1 list-disc space-y-0.5 pl-5">
        {tratamientos.map((t) => <li key={`${t.paciente}|${t.disciplina}`}>{textoTratamiento(t)}.</li>)}
      </ul>
    </div>
  );
}

// Seguimiento de lo que el profesional dice que tiene contra lo que tiene, en
// sesiones por semana (un paciente dos veces son dos): la cantidad que escribió
// en el formulario, en grande, junto a lo que marcó en la grilla y a lo que
// tiene hoy en la agenda general. Debajo, cada paciente con distinta cantidad.
function CantidadPacientes({ planilla }) {
  if (planilla.cantidadPacientes == null) return null;
  const dijo = planilla.cantidadPacientes;
  const agenda = planilla.enAgenda;
  const enAgenda = agenda ? (agenda.sesiones ?? agenda.total ?? agenda.asociados) : null;
  const enGrilla = planilla.sesiones ?? planilla.pacientes;
  const coincide = enAgenda == null || dijo === enAgenda;
  const diferencia = (a, b) => (a === b ? 'coincide' : `diferencia de ${Math.abs(a - b)}`);
  const distintos = (n) => (n == null ? '' : ` · ${n} paciente${n === 1 ? '' : 's'} distinto${n === 1 ? '' : 's'}`);
  const dato = (titulo, valor, aclaracion, destacado) => (
    <div className={`rounded-xl p-3 ${destacado ? (coincide ? 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100' : 'bg-rose-100 text-rose-900 dark:bg-rose-950 dark:text-rose-100') : 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-100'}`}>
      <p className="text-xs font-bold uppercase tracking-wide opacity-80">{titulo}</p>
      <p className={`${destacado ? 'text-5xl' : 'text-3xl'} font-extrabold leading-tight`}>{valor}</p>
      {aclaracion && <p className="text-xs opacity-80">{aclaracion}</p>}
    </div>
  );
  const diferencias = agenda?.diferencias || [];
  return (
    <section aria-label="Cantidad de pacientes" className="space-y-2">
      <div className="grid gap-2 sm:grid-cols-3">
        {dato('Dijo que tiene', dijo, 'sesiones por semana, según el formulario', true)}
        {enGrilla != null && dato('Marcó en la grilla', enGrilla,
          `${diferencia(dijo, enGrilla)} con lo que dijo${planilla.sesiones != null ? distintos(planilla.pacientes) : ''}`)}
        {enAgenda != null && dato('Tiene en la agenda', enAgenda,
          `${enGrilla != null ? `${diferencia(enGrilla, enAgenda)} con la grilla` : diferencia(dijo, enAgenda)}${agenda.sesiones != null ? distintos(agenda.total) : ''}${agenda.sinAsociar ? ` · ${agenda.sinAsociar} sin asociar (en rojo)` : ''}`)}
      </div>
      {diferencias.length > 0 && (
        <div className="text-xs text-slate-700 dark:text-slate-200">
          <p className="font-bold">Pacientes con distinta cantidad de sesiones ({diferencias.length})</p>
          <ul className="mt-1 grid gap-0.5 sm:grid-cols-2">
            {diferencias.map((d) => (
              <li key={d.nombre}><span className="font-semibold">{d.nombre}</span>: {d.grilla} en la grilla, {d.agenda} en la agenda</li>
            ))}
          </ul>
        </div>
      )}
      {enGrilla != null && dijo !== enGrilla && (
        <p className="text-xs text-slate-500">El formulario solo trae el número que escribió el profesional: no dice a qué pacientes se refiere.</p>
      )}
    </section>
  );
}

// Con conGrilla (la ventana de la tarjeta) la planilla completa se carga al abrir,
// y se vuelve a cargar si la agenda cambia de versión o de estado.
function PlanillaProfesional({ planilla, admin, alCambiar, conGrilla = false }) {
  const [detalle, setDetalle] = useState(null);
  const [recarga, setRecarga] = useState(0);
  // Después de guardar o actuar: en la ventana se vuelve a cargar; si no, se pliega.
  const refrescarDetalle = () => (conGrilla ? setRecarga((n) => n + 1) : setDetalle(null));
  useEffect(() => {
    if (!conGrilla) return undefined;
    let vigente = true;
    obtenerPlanillaProfesionalApi(planilla.id)
      .then((d) => { if (vigente) setDetalle(d); })
      .catch((err) => { if (vigente) setError(String(err?.message || 'No se pudo abrir la planilla.')); });
    return () => { vigente = false; };
  }, [conGrilla, planilla.id, planilla.version, planilla.estado, recarga]);
  // Los cambios para revisar empiezan plegados: se ve el listado y se abre el que se quiere revisar.
  const [verCambios, setVerCambios] = useState(false);
  const [asignaciones, setAsignaciones] = useState({});
  const [error, setError] = useState('');
  const [editando, setEditando] = useState(false);
  const [aviso, setAviso] = useState('');
  const nuevos = planilla.diferencias.filter((d) => d.tipo === 'nuevo');
  const faltaAsignar = nuevos.some((d) => !asignaciones[claveHorario(d)]);
  const hayConflictos = planilla.diferencias.some((d) => d.tipo === 'conflicto');
  const tratamientos = planilla.tratamientos || [];
  const estado = ESTADO_PLANILLA[planilla.estado] || ESTADO_PLANILLA.pendiente;
  const actuar = async (accion) => {
    setError('');
    try {
      await accion();
      refrescarDetalle();
      await alCambiar();
    } catch (err) {
      setError(String(err?.message || 'No se pudo completar la acción.'));
    }
  };
  return (
    <article className={`${tarjeta} space-y-3 p-4 ${planilla.estado === 'pendiente' ? 'border-amber-300' : ''}`}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-bold text-slate-900 dark:text-white">{planilla.profesional}</h3>
        <span className="text-sm text-slate-500">
          {planilla.mes} · versión {planilla.version} · {planilla.horarios} horarios con turno
        </span>
        <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${estado.clase}`}>{estado.texto}</span>
        {planilla.estado === 'pendiente' && (
          <button type="button" className={`${botonPrimario} ml-auto`} aria-expanded={verCambios} onClick={() => setVerCambios((v) => !v)}>
            {verCambios ? 'Ocultar cambios' : `Revisar ${planilla.diferencias.length === 1 ? '1 cambio' : `${planilla.diferencias.length} cambios`}`}
          </button>
        )}
        <button type="button" className={`${botonSecundario} ${planilla.estado === 'pendiente' ? '' : 'ml-auto'}`} aria-expanded={Boolean(detalle)} onClick={async () => {
          if (detalle) { setDetalle(null); return; }
          setError('');
          try {
            setDetalle(await obtenerPlanillaProfesionalApi(planilla.id));
          } catch (err) {
            setError(String(err?.message || 'No se pudo abrir la planilla.'));
          }
        }}>
          {detalle ? 'Ocultar planilla' : 'Ver planilla e historial'}
        </button>
        {admin && (
          <button type="button" className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-rose-600 px-3 py-2 text-sm font-bold text-white hover:bg-rose-700"
            aria-label={`Eliminar la agenda de ${planilla.profesional}`} onClick={() => {
              if (window.confirm(`¿Eliminar la agenda de ${planilla.profesional} de ${planilla.mes} con su historial? La agenda general no cambia.`)) {
                actuar(() => eliminarPlanillaProfesionalApi(planilla.id));
              }
            }}>Eliminar</button>
        )}
      </div>
      <CantidadPacientes planilla={planilla} />
      {planilla.motivo && <p className="text-sm text-rose-700">Motivo del rechazo: {planilla.motivo}</p>}
      {planilla.estado === 'pendiente' && <AvisoTratamientos tratamientos={tratamientos} />}
      {hayConflictos && <p role="alert" className="text-sm font-semibold text-rose-700">No se puede aprobar: hay casillas con más de un profesional. Corregí los encabezados en la agenda general y volvé a abrir Profesionales.</p>}
      {planilla.estado === 'pendiente' && verCambios && (
        <>
          <DiferenciasPlanilla diferencias={planilla.diferencias} asignaciones={asignaciones}
            alAsignar={admin ? (clave, valor) => setAsignaciones((a) => ({ ...a, [clave]: valor })) : null} />
          {admin && (
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" className={botonPrimario} disabled={faltaAsignar || hayConflictos} onClick={() => {
                const aviso = tratamientos.length
                  ? `Atención, error de tratamientos:\n${tratamientos.map((t) => `• ${textoTratamiento(t)}`).join('\n')}\n\n`
                  : '';
                if (window.confirm(`${aviso}¿Pasar los cambios de ${planilla.profesional} a la agenda general?`)) {
                  actuar(() => aprobarPlanillaProfesionalApi(planilla.id, asignaciones));
                }
              }}>Aprobar</button>
              <button type="button" className={botonSecundario} onClick={() => {
                const motivo = window.prompt('Motivo del rechazo (opcional)');
                if (motivo !== null) actuar(() => rechazarPlanillaProfesionalApi(planilla.id, motivo));
              }}>Rechazar</button>
              <button type="button" className={botonSecundario} onClick={() => {
                const motivo = window.prompt(`Mensaje para ${planilla.profesional}: le llega por email con un formulario nuevo para que vuelva a cargar la agenda.`);
                if (motivo === null) return;
                if (!motivo.trim()) { setError('Escribí el mensaje para devolver la agenda.'); return; }
                actuar(async () => {
                  await rechazarPlanillaProfesionalApi(planilla.id, motivo);
                  try {
                    await pedirEnvioFormulariosApi([planilla.profesional], planilla.mes);
                  } catch (err) {
                    throw new Error(`La agenda quedó rechazada, pero no se pudo pedir el email: ${err?.message || 'error'}`);
                  }
                });
              }}>Devolver con mensaje</button>
              {faltaAsignar && <span className="text-xs text-amber-700">Para aprobar, elegí un consultorio en cada horario que no está en la agenda (o "No pasar a la agenda").</span>}
            </div>
          )}
        </>
      )}
      {aviso && <p role="status" className="text-sm font-semibold text-emerald-700">{aviso}</p>}
      {detalle && (
        <div className="space-y-3 rounded-xl bg-slate-50 p-3 dark:bg-slate-900/40">
          {admin && !editando && (
            <button type="button" className={botonSecundario} onClick={() => { setAviso(''); setEditando(true); }}>Editar planilla</button>
          )}
          {editando ? (
            <EditorPlanilla planilla={planilla} turnos={detalle.turnos || []} alCancelar={() => setEditando(false)}
              enfocar={typeof editando === 'string' ? editando : null}
              alTerminar={async (mensaje) => {
                setEditando(false);
                refrescarDetalle();
                setAviso(mensaje);
                await alCambiar();
              }} />
          ) : (
            <GrillaPlanilla turnos={detalle.turnos || []} diferencias={detalle.diferencias || []}
              alEditar={admin ? (clave) => { setAviso(''); setEditando(clave); } : undefined} />
          )}
          <div>
            <h4 className="text-xs font-bold uppercase text-slate-500">Historial</h4>
            <ul className="mt-1 space-y-1 text-xs text-slate-600 dark:text-slate-300">
              {detalle.historial.map((h, i) => (
                <li key={i}>
                  {fechaHora(h.fecha)} · {ACCION_HISTORIAL[h.accion] || h.accion} (versión {h.version})
                  {h.usuario && ` por ${h.usuario}`} · {h.horarios} horarios
                  {h.cantidadPacientes != null && ` · informó ${h.cantidadPacientes} pacientes`}
                  {h.diferencias != null && ` · ${h.diferencias} diferencias con la general`}
                  {h.motivo && ` · ${h.motivo}`}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      {error && <p className="text-sm font-semibold text-rose-600">{error}</p>}
    </article>
  );
}

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const nombreMes = (mes) => (mes ? `${MESES[Number(mes.slice(5, 7)) - 1]} ${mes.slice(0, 4)}` : '');
const ESTADO_PEDIDO = {
  pendiente: 'Esperando a Google: revisa los pedidos cada 5 minutos.',
  en_curso: 'Google está armando los formularios y mandando los emails.',
  listo: 'Envío terminado.',
  error: 'El envío tuvo problemas.',
};


// Lista de profesionales y envío de sus formularios precargados en una sola
// tabla paginada. Los nombres se usan para asignar profesionales en la agenda;
// los administradores cargan acá el email de cada uno y piden el envío. Google
// toma el pedido en la próxima revisión y avisa a quién se lo mandó.
function ProfesionalesYFormularios({ admin, alCambiar }) {
  const [profesionales, setProfesionales] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [envios, setEnvios] = useState(null);
  const [emails, setEmails] = useState({});
  const [busqueda, setBusqueda] = useState('');
  const [elegido, setElegido] = useState(null);
  // Datos del profesional abierto, cuando se toca su nombre para modificarlos.
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [errorEnvio, setErrorEnvio] = useState('');
  const [mensajeEnvio, setMensajeEnvio] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const cargarCatalogo = useCallback(async () => {
    try { setProfesionales(await obtenerCatalogoProfesionalesApi()); }
    catch (err) { setError(err.message || 'No se pudo cargar la lista de profesionales.'); }
    finally { setCargando(false); }
  }, []);
  // Los emails ya escritos y sin guardar se conservan al recargar.
  const cargarEnvios = useCallback(async () => {
    if (!admin) return;
    try {
      const r = await obtenerEnviosFormularioApi();
      setEnvios(r);
      setEmails((prev) => ({ ...Object.fromEntries(r.profesionales.map((p) => [p.profesional, p.email])), ...prev.sinGuardar }));
    } catch (err) {
      setErrorEnvio(String(err?.message || 'No se pudieron cargar los emails.'));
    }
  }, [admin]);
  useEffect(() => { cargarCatalogo(); }, [cargarCatalogo]);
  useEffect(() => { cargarEnvios(); }, [cargarEnvios]);
  // Mientras Google trabaja, el estado del envío se actualiza solo.
  const activo = ['pendiente', 'en_curso'].includes(envios?.pedido?.estado);
  useEffect(() => {
    if (!activo) return undefined;
    const id = setInterval(cargarEnvios, 60000);
    return () => clearInterval(id);
  }, [activo, cargarEnvios]);

  const envioDe = useMemo(() => new Map((envios?.profesionales || []).map((p) => [normalizar(p.profesional), p])), [envios]);
  const visibles = useMemo(() => profesionales
    .filter((p) => normalizar(p.nombre).includes(normalizar(busqueda).replace(/\s+/g, ' ')))
    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' })), [profesionales, busqueda]);

  const recargarTodo = async () => { await cargarCatalogo(); await cargarEnvios(); await alCambiar(); };
  const abrirDatos = async (p) => {
    setError(''); setMensaje('');
    try {
      const encontrado = (await obtenerProfesionalesApi()).find((x) => x.id === p.id);
      if (!encontrado) throw new Error('Ese profesional ya no está en la lista.');
      setDatos(encontrado);
    } catch (err) { setError(err.message || 'No se pudieron cargar sus datos.'); }
  };
  const datosGuardados = async (p) => {
    setDatos(null);
    setMensaje(`Datos de ${p.nombre} guardados.`);
    // Los emails escritos y sin guardar de este profesional se descartan: manda lo guardado.
    setEmails((prev) => { const sinGuardar = { ...prev.sinGuardar }; delete sinGuardar[p.nombre]; return { ...prev, sinGuardar }; });
    await recargarTodo();
  };

  const ejecutar = async (accion) => {
    setOcupado(true);
    setErrorEnvio('');
    setMensajeEnvio('');
    try {
      await accion();
      await cargarEnvios();
    } catch (err) {
      setErrorEnvio(String(err?.message || 'No se pudo completar.'));
    } finally {
      setOcupado(false);
    }
  };
  const emailDe = (p) => emails[p.profesional] || '';
  const cambiados = (envios?.profesionales || []).filter((p) => emailDe(p) !== p.email);
  const guardarEmails = async () => {
    await guardarEmailsProfesionalesApi((envios?.profesionales || []).map((p) => ({ profesional: p.profesional, email: emailDe(p) })));
    setEmails((prev) => ({ ...prev, sinGuardar: {} }));
  };
  const cambiarEmail = (profesional, valor) => setEmails((prev) => ({
    ...prev, [profesional]: valor, sinGuardar: { ...prev.sinGuardar, [profesional]: valor },
  }));
  const enviar = (lista) => {
    // Antes de enviar se muestra a quién le va a llegar, con el email tal como está escrito.
    const para = (envios?.profesionales || []).filter((p) => emailDe(p).trim()
      && (lista ? lista.includes(p.profesional) : !p.enviadoAt));
    if (!para.length) {
      setErrorEnvio('No hay profesionales con email a los que falte mandarles el formulario.');
      return;
    }
    const detalle = para.map((p) => `- ${p.profesional}: ${emailDe(p).trim()}`).join('\n');
    if (!window.confirm(`Se va a mandar el formulario de ${nombreMes(envios.mes)} a:\n${detalle}\n\n¿Enviar?`)) return;
    ejecutar(async () => {
      if (cambiados.length) await guardarEmails();
      const r = await pedirEnvioFormulariosApi(lista);
      setMensajeEnvio(`Envío pedido para: ${(r.nombres || []).join(', ')}. Google empieza en menos de 5 minutos y manda cada email apenas está listo.`);
    });
  };
  const descargarScript = async () => {
    try {
      const r = await obtenerScriptFormularioProfesionalesApi();
      descargar(r.script, r.nombre, 'text/javascript;charset=utf-8');
    } catch (err) {
      setErrorEnvio(String(err?.message || 'No se pudo generar el script.'));
    }
  };
  const pedido = envios?.pedido;

  return (
    <section className={`${tarjeta} space-y-4 p-5`} aria-label="Lista de profesionales">
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0">
          <h3 className="font-bold text-slate-900 dark:text-white">Profesionales y formularios</h3>
          <p className="text-sm text-slate-500">
            Estos nombres se usan para asignar profesionales en la agenda. Para agregar o eliminar profesionales, usá la solapa Profesionales.
            {admin && envios && ` Cargá el email de cada uno para mandarle su formulario de ${nombreMes(envios.mes)}.`}
          </p>
        </div>
        {admin && (
          <button type="button" className={`${botonSecundario} ml-auto`} onClick={descargarScript}>
            <span className="material-symbols-outlined text-base">download</span>Script de los formularios
          </button>
        )}
      </div>
      {admin && (
        <details className="text-sm text-slate-500">
          <summary className="cursor-pointer font-semibold">Cómo instalar el script de Google</summary>
          <p className="mt-1">
            Descargá el script, abrí script.google.com, creá un proyecto (o abrí el que ya tenés), pegalo, guardalo y ejecutá <strong>instalar</strong> aceptando los permisos.
            Desde ahí los formularios se envían desde acá. Cada vez que se actualiza el script, pegalo de nuevo y volvé a ejecutar <strong>instalar</strong>.
          </p>
          <p className="mt-1 text-xs">El script tiene la clave de envío y los enlaces llevan nombres de pacientes: no los compartas.</p>
        </details>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <label className="min-w-[12rem] flex-1 text-xs font-bold text-slate-600 dark:text-slate-300">
          <span className="mb-1 block">Buscar profesional</span>
          <input type="search" className={`${inputClass} w-full`} value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)} placeholder="Nombre o apellido" autoComplete="off" />
        </label>
      </div>
      {error && <p role="alert" className="text-sm font-semibold text-rose-700">{error}</p>}
      {mensaje && <p role="status" className="text-sm font-semibold text-emerald-700">{mensaje}</p>}

      {cargando ? <p className="text-sm text-slate-500">Cargando profesionales…</p> : !profesionales.length ? (
        <p className="text-sm text-slate-500">Todavía no hay profesionales. Agregá el primero para empezar.</p>
      ) : !visibles.length ? (
        <p role="status" className="text-sm text-slate-500">No se encontraron profesionales con ese nombre.</p>
      ) : (
        <>
          {admin && envios && (
            <p className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-emerald-500" />Enviado</span>
              <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-slate-300" />Sin enviar</span>
              <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-sm border-2 border-amber-500" />Sin email</span>
              <span>Tocá un profesional para ver su email y enviarle el formulario.</span>
            </p>
          )}
          <ColumnasPorArea items={visibles} disciplinaDe={(p) => envioDe.get(normalizar(p.nombre))?.disciplina || ''} etiqueta="Profesionales por área">
            {(p) => {
              const envio = envioDe.get(normalizar(p.nombre));
              const conEmail = Boolean(envio && emailDe(envio).trim());
              const cuadrado = !admin || !envios ? 'bg-slate-200' : envio?.enviadoAt ? 'bg-emerald-500' : conEmail ? 'bg-slate-300' : 'border-2 border-amber-500';
              const estado = !admin || !envios ? '' : envio?.enviadoAt ? `Enviado ${fechaHora(envio.enviadoAt)}` : conEmail ? 'Sin enviar' : 'Sin email';
              return (
                <li key={p.id}>
                  <button type="button" onClick={() => { setElegido(p.id); setDatos(null); }} aria-label={`Abrir ${p.nombre}`}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700/50">
                    <span className={`h-3 w-3 shrink-0 rounded-sm ${cuadrado}`} title={estado} aria-hidden="true" />
                    <span className="min-w-0 flex-1 truncate font-semibold text-slate-800 dark:text-slate-100">{p.nombre}</span>
                    {estado && <span className="sr-only">{estado}</span>}
                  </button>
                </li>
              );
            }}
          </ColumnasPorArea>
        </>
      )}
      {(() => {
        const p = elegido && profesionales.find((x) => x.id === elegido);
        if (!p) return null;
        const envio = envioDe.get(normalizar(p.nombre));
        return (
          <Ventana titulo={p.nombre} alCerrar={() => { setElegido(null); setDatos(null); }}>
            <section className={`${tarjeta} space-y-3 p-5`}>
              {admin ? (
                <button type="button" onClick={() => (datos?.id === p.id ? setDatos(null) : abrirDatos(p))} aria-expanded={datos?.id === p.id}
                  aria-label={`Modificar datos de ${p.nombre}`} title="Modificar sus datos"
                  className="inline-flex items-center gap-1 pr-10 text-left text-lg font-bold text-slate-900 hover:text-emerald-700 dark:text-white">
                  {p.nombre}<span className="material-symbols-outlined text-base text-slate-400">edit</span>
                </button>
              ) : (
                <h3 className="pr-10 text-lg font-bold text-slate-900 dark:text-white">{p.nombre}</h3>
              )}
              {admin && datos?.id === p.id && (
                <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
                  <FormularioProfesional key={p.id} profesional={datos} alGuardado={datosGuardados} alCancelar={() => setDatos(null)} />
                </div>
              )}
              {envio?.disciplina && <p className="text-sm text-slate-500">{envio.disciplina}</p>}
              {admin && envios && envio && (
                <label className="block text-xs font-bold text-slate-600 dark:text-slate-300">
                  <span className="mb-1 block">Email</span>
                  {/* Texto y sin autocompletar: el navegador llenaba con tu email las filas sin que se notara. */}
                  <input type="text" inputMode="email" autoComplete="off" spellCheck={false} name={`contacto-${p.id}`} data-lpignore="true"
                    className={`${inputClass} w-full`} value={emailDe(envio)}
                    aria-label={`Email de ${envio.profesional}`} placeholder="email@ejemplo.com"
                    onChange={(e) => cambiarEmail(envio.profesional, e.target.value)} />
                </label>
              )}
              {admin && envios && (
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Formulario de {nombreMes(envios.mes)}: {envio?.enviadoAt ? `enviado ${fechaHora(envio.enviadoAt)}` : 'sin enviar'}
                </p>
              )}
              {admin && (
                <div className="flex flex-wrap gap-2">
                  {envio && cambiados.some((c) => c.profesional === envio.profesional) && (
                    <button type="button" className={botonSecundario} disabled={ocupado}
                      onClick={() => ejecutar(async () => { await guardarEmails(); setMensajeEnvio('Emails guardados.'); })}>Guardar email</button>
                  )}
                  {/* Envío individual: solo a este profesional, aunque no se le mande a todos. */}
                  {envio && emailDe(envio).trim() && (
                    <button type="button" className={botonPrimario} disabled={ocupado || activo}
                      aria-label={`${envio.enviadoAt ? 'Reenviar' : 'Enviar'} formulario a ${envio.profesional}`}
                      onClick={() => enviar([envio.profesional])}>
                      {envio.enviadoAt ? 'Reenviar' : 'Enviar'}
                    </button>
                  )}
                </div>
              )}
              {error && <p role="alert" className="text-sm font-semibold text-rose-700">{error}</p>}
              {mensaje && <p role="status" className="text-sm font-semibold text-emerald-700">{mensaje}</p>}
              {mensajeEnvio && <p className="text-sm font-semibold text-emerald-700">{mensajeEnvio}</p>}
              {errorEnvio && <p className="text-sm font-semibold text-rose-600">{errorEnvio}</p>}
            </section>
          </Ventana>
        );
      })()}

      {admin && envios && (
        <div className="space-y-2 border-t border-slate-100 pt-3 dark:border-slate-700">
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className={botonSecundario} disabled={ocupado || !cambiados.length}
              onClick={() => ejecutar(async () => { await guardarEmails(); setMensajeEnvio('Emails guardados.'); })}>
              Guardar emails
            </button>
            <button type="button" className={botonPrimario} disabled={ocupado || activo} onClick={() => enviar(null)}>
              Enviar formularios de {nombreMes(envios.mes)}
            </button>
            <span className="text-xs text-slate-500">
              {activo ? 'Hay un envío en curso: esperá a que termine o cancelalo.' : 'Se manda a quienes tienen email y todavía no lo recibieron este mes.'}
            </span>
          </div>
          {pedido && (
            <p className={`text-sm ${pedido.estado === 'error' ? 'text-rose-600' : 'text-slate-600 dark:text-slate-300'}`} role="status">
              {ESTADO_PEDIDO[pedido.estado]} {pedido.mensaje} <span className="text-xs text-slate-400">({fechaHora(pedido.actualizadoAt)})</span>
              {activo && (
                <button type="button" className={`${botonSecundario} ml-2`} disabled={ocupado}
                  onClick={() => window.confirm('¿Cancelar este envío? Los emails que Google ya mandó no se recuperan.')
                    && ejecutar(async () => { await cancelarEnvioFormulariosApi(pedido.id); setMensajeEnvio('Envío cancelado.'); })}>
                  Cancelar envío
                </button>
              )}
            </p>
          )}
          {mensajeEnvio && <p className="text-sm font-semibold text-emerald-700">{mensajeEnvio}</p>}
        </div>
      )}
      {errorEnvio && <p className="text-sm font-semibold text-rose-600">{errorEnvio}</p>}
    </section>
  );
}

// Columnas por área (Fono, PSP, Psico, PSM, Kine, T.O.): arriba el área y
// debajo una tarjeta chica por profesional.
function ColumnasPorArea({ items, disciplinaDe, etiqueta, children }) {
  const grupos = new Map();
  items.forEach((item) => {
    const area = areaDe(disciplinaDe(item));
    if (!grupos.has(area)) grupos.set(area, []);
    grupos.get(area).push(item);
  });
  const areas = ORDEN_AREAS.filter((a) => grupos.has(a));
  return (
    <div className="grid items-start gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4" aria-label={etiqueta}>
      {areas.map((area) => (
        <section key={area} aria-label={area} className={`${tarjeta} overflow-hidden`}>
          <h4 className="border-b border-slate-200 bg-slate-100 px-3 py-1.5 text-sm font-extrabold text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100">
            {area} <span className="font-normal text-slate-500">· {grupos.get(area).length}</span>
          </h4>
          <ul className="divide-y divide-slate-100 dark:divide-slate-700">
            {grupos.get(area).map((item) => children(item))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function AgendasProfesionales({ admin, alCambiar }) {
  // Sin mes elegido se abre el de la primera agenda pendiente (o la más nueva):
  // los formularios suelen ser del mes siguiente, no del actual.
  const [mes, setMes] = useState('');
  const [planillas, setPlanillas] = useState(null);
  const [todas, setTodas] = useState([]);
  const [error, setError] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [abierta, setAbierta] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const lista = await obtenerPlanillasProfesionalesApi();
      setTodas(lista || []);
      const elegido = mes || lista?.[0]?.mes || mesActual();
      if (!mes) setMes(elegido);
      setPlanillas((lista || []).filter((p) => p.mes === elegido));
      setError('');
    } catch (err) {
      setError(String(err?.message || 'No se pudieron cargar las agendas de profesionales.'));
    }
  }, [mes]);
  useEffect(() => { cargar(); }, [cargar]);
  const recargar = async () => { await cargar(); await alCambiar(); };

  const pendientes = (planillas || []).filter((p) => p.estado === 'pendiente');
  // Cuántos profesionales tienen la agenda del mes en cada estado; tocar uno filtra la lista.
  const porEstado = Object.keys(ESTADO_PLANILLA).map((estado) => ({ estado, cantidad: (planillas || []).filter((p) => p.estado === estado).length }));
  const visibles = (planillas || []).filter((p) => !filtroEstado || p.estado === filtroEstado);
  const otrosMeses = Array.from(new Set(todas.filter((p) => p.estado === 'pendiente' && p.mes !== mes).map((p) => p.mes))).sort();
  return (
    <div className="space-y-4">
      {/* Primero las agendas que entregaron; el envío de emails va al final. */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="text-xs font-bold text-slate-600 dark:text-slate-300">
          <span className="mb-1 block">Mes</span>
          <InputMes value={mes} onChange={setMes} />
        </div>
        <p className="text-xs text-slate-500">Las planillas se guardan aparte de la agenda general y se borran solas a los 6 meses.</p>
      </div>
      {otrosMeses.length > 0 && (
        <p className="flex flex-wrap items-center gap-2 text-sm text-amber-900">
          También hay agendas pendientes en
          {otrosMeses.map((m) => (
            <button key={m} type="button" className={botonSecundario} onClick={() => setMes(m)}>{nombreMes(m)}</button>
          ))}
        </p>
      )}
      {error && <p className="text-sm font-semibold text-rose-600">{error}</p>}
      {pendientes.length > 0 && (
        <p className="rounded-2xl border border-amber-300 bg-amber-50 p-3 text-sm font-semibold text-amber-900" role="alert">
          {pendientes.length === 1 ? 'Hay 1 agenda con cambios' : `Hay ${pendientes.length} agendas con cambios`} que esperan aprobación.
        </p>
      )}
      {planillas && !planillas.length && (
        <div className={`${tarjeta} p-8 text-center text-sm text-slate-500`}>No hay agendas de profesionales cargadas para {nombreMes(mes)}.</div>
      )}
      {planillas && planillas.length > 0 && (
        <section aria-label="Agendas por estado" className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-bold text-slate-700 dark:text-slate-200">{planillas.length} {planillas.length === 1 ? 'profesional respondió' : 'profesionales respondieron'}:</span>
          {porEstado.map(({ estado, cantidad }) => (
            <button key={estado} type="button" aria-pressed={filtroEstado === estado} disabled={!cantidad}
              className={`rounded-full px-3 py-1 text-xs font-bold disabled:opacity-40 ${ESTADO_PLANILLA[estado].clase} ${filtroEstado === estado ? 'ring-2 ring-slate-500' : ''}`}
              onClick={() => setFiltroEstado((f) => (f === estado ? '' : estado))}>
              {ESTADO_PLANILLA[estado].texto}: {cantidad}
            </button>
          ))}
          {filtroEstado && <button type="button" className="text-xs font-bold text-slate-500 underline" onClick={() => setFiltroEstado('')}>Ver todas</button>}
        </section>
      )}
      {/* Tarjetas chicas por área; al tocar una se abre la agenda completa en grande. */}
      {visibles.length > 0 && (
        <ColumnasPorArea items={visibles} disciplinaDe={(p) => p.disciplina} etiqueta="Agendas por área">
          {(p) => {
            const estado = ESTADO_PLANILLA[p.estado] || ESTADO_PLANILLA.pendiente;
            const cambios = (p.diferencias || []).length;
            return (
              <li key={p.id}>
                <button type="button" onClick={() => setAbierta(p.id)} aria-label={`Abrir agenda de ${p.profesional}`}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700/50">
                  <span className={`h-3 w-3 shrink-0 rounded-sm ${CUADRADO_ESTADO[p.estado] || CUADRADO_ESTADO.pendiente}`} title={estado.texto} aria-hidden="true" />
                  <span className="min-w-0 flex-1 truncate font-semibold text-slate-800 dark:text-slate-100">{p.profesional}</span>
                  {cambios > 0 && (
                    <span className="shrink-0 rounded bg-amber-200 px-1.5 text-xs font-bold text-amber-900" title="Cambios para revisar">{cambios}</span>
                  )}
                  <span className="sr-only">{estado.texto}</span>
                </button>
              </li>
            );
          }}
        </ColumnasPorArea>
      )}
      {(() => {
        const elegida = abierta && (planillas || []).find((p) => p.id === abierta);
        return elegida ? (
          <Ventana titulo={`Agenda de ${elegida.profesional}`} alCerrar={() => setAbierta(null)}>
            <PlanillaProfesional key={elegida.id} planilla={elegida} admin={admin} alCambiar={recargar} conGrilla />
          </Ventana>
        ) : null;
      })()}
      <ProfesionalesYFormularios admin={admin} alCambiar={recargar} />
    </div>
  );
}

function Configuracion({ sedes, alCambiar }) {
  const [csv, setCsv] = useState('');
  const [archivo, setArchivo] = useState('');
  const [vista, setVista] = useState(null);
  const [mensaje, setMensaje] = useState('');
  const [error, setError] = useState('');
  const [sedeId, setSedeId] = useState('');
  const [consultorios, setConsultorios] = useState([]);
  const [nuevo, setNuevo] = useState('');

  useEffect(() => {
    if (!sedeId && sedes.length) setSedeId(String(sedes[0].id));
  }, [sedes, sedeId]);
  const cargarConsultorios = useCallback(async () => {
    if (!sedeId) return;
    try {
      setConsultorios((await obtenerAgendaSemanaApi(sedeId)).consultorios);
    } catch (err) {
      setError(String(err?.message || 'No se pudieron cargar los consultorios.'));
    }
  }, [sedeId]);
  useEffect(() => { cargarConsultorios(); }, [cargarConsultorios]);

  const ejecutar = async (accion) => {
    setError('');
    setMensaje('');
    try {
      await accion();
    } catch (err) {
      setError(String(err?.message || 'No se pudo completar la acción.'));
    }
  };
  const yaCargada = vista?.sedes?.some((s) => s.yaCargada);

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <section className={`${tarjeta} space-y-3 p-5`}>
        <h3 className="font-bold text-slate-900 dark:text-white">Carga inicial desde la planilla</h3>
        <p className="text-sm text-slate-500">
          Se hace una sola vez: en Google Sheets, Archivo → Descargar → Valores separados por comas (.csv). Después la agenda se edita acá.
        </p>
        <input type="file" accept=".csv,text/csv" aria-label="Archivo CSV de la agenda" className="text-sm"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            setVista(null);
            if (!file) return;
            setArchivo(file.name);
            const texto = await file.text();
            setCsv(texto);
            ejecutar(async () => setVista(await importarAgendaApi(texto)));
          }} />
        {vista && (
          <div className="space-y-2 text-sm">
            {vista.sedes.map((s) => (
              <div key={s.sede} className="rounded-xl bg-slate-50 p-3 dark:bg-slate-900/40">
                <p className="font-bold">{s.sede} {s.yaCargada && <span className="text-xs font-semibold text-amber-700">(ya tiene agenda: se reemplaza)</span>}</p>
                <p className="text-xs text-slate-500">
                  {s.consultorios.length} consultorios · {s.consultorios.reduce((a, c) => a + c.turnos, 0)} turnos ocupados ·{' '}
                  {s.consultorios.map((c) => c.nombre).join(', ')}
                </p>
              </div>
            ))}
            <button type="button" className={botonPrimario} onClick={() => ejecutar(async () => {
              if (yaCargada && !window.confirm('Esto reemplaza la agenda actual, con los cambios hechos en Cenein. ¿Continuar?')) return;
              const r = await importarAgendaApi(csv, { confirmar: true, reemplazar: yaCargada });
              setVista(null);
              setMensaje(`Agenda cargada desde ${archivo}: ${r.sedes.map((s) => `${s.sede} (${s.asociados} asociados, ${s.sinAsociar} sin asociar)`).join(', ')}.`);
              await alCambiar();
            })}>
              Confirmar carga
            </button>
          </div>
        )}
        {mensaje && <p className="text-sm font-semibold text-emerald-700">{mensaje}</p>}
        {error && <p className="text-sm font-semibold text-rose-600">{error}</p>}
      </section>

      <section className={`${tarjeta} space-y-3 p-5`}>
        <h3 className="font-bold text-slate-900 dark:text-white">Consultorios</h3>
        <select className={inputClass} value={sedeId} onChange={(e) => setSedeId(e.target.value)} aria-label="Centro">
          {sedes.map((s) => <option key={s.id} value={String(s.id)}>{s.nombre}</option>)}
        </select>
        <ul className="space-y-1">
          {consultorios.map((c) => (
            <li key={c.id} className="flex items-center justify-between gap-2 text-sm">
              <span className="font-semibold">{c.nombre}</span>
              <span className="flex gap-3">
                <button type="button" className="text-xs font-bold text-emerald-700" onClick={() => {
                  const nombre = window.prompt('Nuevo nombre del consultorio', c.nombre);
                  if (nombre && nombre.trim() !== c.nombre) ejecutar(async () => { await renombrarConsultorioApi(c.id, nombre.trim()); await cargarConsultorios(); });
                }}>Renombrar</button>
                <button type="button" className="text-xs font-bold text-rose-600" onClick={() => {
                  if (window.confirm(`¿Eliminar ${c.nombre} con todos sus turnos?`)) ejecutar(async () => { await eliminarConsultorioApi(c.id); await cargarConsultorios(); });
                }}>Eliminar</button>
              </span>
            </li>
          ))}
        </ul>
        <form className="flex gap-2" onSubmit={(e) => {
          e.preventDefault();
          if (!nuevo.trim() || !sedeId) return;
          ejecutar(async () => { await crearConsultorioApi(Number(sedeId), nuevo.trim()); setNuevo(''); await cargarConsultorios(); });
        }}>
          <input className={`${inputClass} flex-1`} value={nuevo} onChange={(e) => setNuevo(e.target.value)} placeholder="Nombre del consultorio" aria-label="Nombre del consultorio" />
          <button type="submit" className={botonPrimario} disabled={!sedeId}>Agregar</button>
        </form>
      </section>
    </div>
  );
}

// Apartado de alertas: junta todos los errores y avisos de agendas en un lugar.
// Lista paginada de a 10, para que las listas largas no hagan lenta la pantalla.
const POR_PAGINA_ALERTAS = 10;
function usePaginado(items) {
  const [pagina, setPagina] = useState(0);
  const paginas = Math.max(1, Math.ceil(items.length / POR_PAGINA_ALERTAS));
  const actual = Math.min(pagina, paginas - 1);
  return { actual, paginas, setPagina, enPagina: items.slice(actual * POR_PAGINA_ALERTAS, (actual + 1) * POR_PAGINA_ALERTAS) };
}

function Paginador({ actual, paginas, setPagina, total, etiqueta }) {
  if (paginas < 2) return null;
  return (
    <nav className="mt-2 flex flex-wrap items-center gap-2 text-xs" aria-label={`Páginas de ${etiqueta}`}>
      <button type="button" className={botonSecundario} disabled={actual === 0} onClick={() => setPagina(actual - 1)}>Anterior</button>
      <span>Página {actual + 1} de {paginas} · {total} en total</span>
      <button type="button" className={botonSecundario} disabled={actual >= paginas - 1} onClick={() => setPagina(actual + 1)}>Siguiente</button>
    </nav>
  );
}

function SeccionAlerta({ titulo, tono, ayuda, pie, children }) {
  const items = React.Children.toArray(children);
  const paginado = usePaginado(items);
  if (!items.length) return null;
  const colores = tono === 'error'
    ? 'border-rose-300 bg-rose-50 text-rose-900 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-100'
    : 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100';
  return (
    <details open className={`rounded-xl border-2 p-4 text-sm ${colores}`}>
      <summary className="cursor-pointer font-bold">
        {titulo} <span className="ml-1 rounded-full bg-white/70 px-2 py-0.5 text-xs dark:bg-black/30">{items.length}</span>
      </summary>
      {ayuda && <p className="mt-1 text-xs opacity-80">{ayuda}</p>}
      <ul className="mt-2 space-y-1">{paginado.enPagina}</ul>
      <Paginador {...paginado} total={items.length} etiqueta={titulo.toLowerCase()} />
      {pie}
    </details>
  );
}

const NOMBRES_MES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

function Alertas({ sedes, pacientes, editable, irAProfesionales }) {
  const [consultorios, setConsultorios] = useState(null);
  const [sinAutorizacion, setSinAutorizacion] = useState([]);
  const [planillas, setPlanillas] = useState([]);
  const [noEncontrados, setNoEncontrados] = useState(0);
  const [error, setError] = useState('');
  const mes = mesActual();
  useEffect(() => {
    let vigente = true;
    (async () => {
      try {
        const [agenda, autorizacion, listado] = await Promise.all([
          cargarAgendaCompleta(sedes),
          obtenerSinAutorizacionAgendaApi(mes),
          obtenerPlanillasProfesionalesApi().catch(() => []),
        ]);
        if (!vigente) return;
        setConsultorios(agenda);
        setSinAutorizacion(autorizacion?.pacientes || []);
        setPlanillas(listado || []);
      } catch (err) {
        if (vigente) setError(String(err?.message || 'No se pudieron cargar las alertas.'));
      }
    })();
    return () => { vigente = false; };
  }, [sedes, mes]);

  const revision = useMemo(() => revisarTratamientos(consultorios || []), [consultorios]);
  const conflictos = useMemo(() => casillasConVariosProfesionales(consultorios || []), [consultorios]);
  const planillasConError = planillas.filter((p) => p.estado === 'pendiente'
    && ((p.tratamientos || []).length || p.diferencias.some((d) => d.tipo === 'conflicto')));
  const detalle = (item) => item.profesionales
    .map((p) => `${p.nombre}: ${p.sesiones.map(textoHorario).join(', ')}`).join(' · ');
  const errores = revision.errores.length + conflictos.length + sinAutorizacion.length + planillasConError.length + noEncontrados;
  const nombreMes = NOMBRES_MES[Number(mes.slice(5, 7)) - 1];

  return (
    <div className="space-y-4">
      {error && <p className="text-sm font-semibold text-rose-600">{error}</p>}
      {consultorios && (
        <p role="status" className={`rounded-xl p-3 text-sm font-semibold ${errores ? 'bg-rose-100 text-rose-900 dark:bg-rose-950 dark:text-rose-100' : 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100'}`}>
          {errores
            ? `${errores === 1 ? 'Hay 1 alerta' : `Hay ${errores} alertas`} para revisar en las agendas${revision.repetidos.length ? `, más ${revision.repetidos.length === 1 ? '1 aviso' : `${revision.repetidos.length} avisos`}` : ''}.`
            : `Sin errores en las agendas${revision.repetidos.length ? ` (${revision.repetidos.length === 1 ? '1 aviso' : `${revision.repetidos.length} avisos`} abajo)` : ''}.`}
        </p>
      )}
      <SeccionAlerta tono="error" titulo="Pacientes con dos profesionales del mismo área"
        ayuda="El mismo paciente tiene el mismo tratamiento con dos profesionales. Revisá cuál corresponde en la agenda general.">
        {revision.errores.map((e) => (
          <li key={`${e.paciente}|${e.disciplina}`}><span className="font-semibold">{e.paciente}</span> · {e.disciplina} · {detalle(e)}</li>
        ))}
      </SeccionAlerta>
      <SeccionAlerta tono="error" titulo="Casillas con más de un profesional"
        ayuda="Cada casilla tiene que tener un solo profesional. Corregí el encabezado en la agenda general.">
        {conflictos.map((t) => <li key={t.id}>{t.consultorio} · {NOMBRE_DIA[t.dia] || t.dia} {t.hora} · {t.profesional}</li>)}
      </SeccionAlerta>
      <SeccionAlerta tono="error" titulo={`Pacientes en la agenda sin autorización en ${nombreMes} o con baja`}
        ayuda="Están en la agenda, pero no tienen autorización vigente este mes o fueron dados de baja. Revisá su autorización o sacalos de la agenda.">
        {sinAutorizacion.map((p) => (
          <li key={p.patientId}>
            <span className="font-semibold">{p.nombre}</span>
            {' '}<span className="rounded bg-white/70 px-1 text-xs font-bold dark:bg-black/30">{p.motivo === 'baja' ? 'Dado de baja' : 'Sin autorización'}</span>
            {' '}· {p.turnos.map((t) => `${t.profesional ? `${t.profesional}: ` : ''}${textoHorario(t)}`).join(', ')}
          </li>
        ))}
      </SeccionAlerta>
      <SeccionAlerta tono="error" titulo="Planillas de profesionales pendientes con errores"
        ayuda="Se revisan y aprueban en Profesionales."
        pie={<button type="button" className={`${botonSecundario} mt-2`} onClick={irAProfesionales}>Ir a Profesionales</button>}>
        {planillasConError.map((p) => (
          <li key={p.id}>
            <span className="font-semibold">{p.profesional}</span> ({p.mes}):{' '}
            {[
              (p.tratamientos || []).length && `${(p.tratamientos || []).map(textoTratamiento).join('; ')}`,
              p.diferencias.some((d) => d.tipo === 'conflicto') && 'tiene casillas con más de un profesional',
            ].filter(Boolean).join('. ')}
          </li>
        ))}
      </SeccionAlerta>
      <SeccionAlerta tono="aviso" titulo="Más de una sesión del mismo área con el mismo profesional"
        ayuda="Es un aviso: puede ser correcto si el paciente tiene más de una sesión semanal.">
        {revision.repetidos.map((r) => (
          <li key={`${r.paciente}|${r.disciplina}`}><span className="font-semibold">{r.paciente}</span> tiene {r.sesiones.length} sesiones de {r.disciplina} · {detalle(r)}</li>
        ))}
      </SeccionAlerta>
      <NoEncontrados pacientes={pacientes} editable={editable} alContar={setNoEncontrados} />
    </div>
  );
}

// Orden fijo de los centros: CENEIN, CENE.DOS y CENE.TITA; otros, después por nombre.
const ORDEN_CENTROS = ['cenein', 'cenedos', 'cenetita'];
const ordenCentro = (nombre) => {
  const i = ORDEN_CENTROS.indexOf(normalizar(nombre).replace(/[^a-z0-9]/g, ''));
  return i < 0 ? ORDEN_CENTROS.length : i;
};
const ordenarCentros = (lista) => [...(lista || [])]
  .sort((a, b) => ordenCentro(a.nombre) - ordenCentro(b.nombre) || String(a.nombre).localeCompare(String(b.nombre), 'es'));

// Planilla de doble entrada: pacientes por fila, profesionales por columna, con
// lo que cada profesional informó en su última respuesta del formulario. Se
// vuelve a pedir cada minuto para que las respuestas nuevas aparezcan solas.
// Áreas de "Pacientes por profesional", en el orden en que se muestran. La
// agrupada es la de las respuestas anteriores a separar Kinesiología,
// Psicomotricidad y T.O.
const AREAS = [
  { nombre: 'Fonoaudiología', es: (d) => d.startsWith('fono') },
  { nombre: 'Psicopedagogía', es: (d) => d.startsWith('psicoped') },
  { nombre: 'Psicología', es: (d) => d.startsWith('psicolog') },
  { nombre: 'Psicomotricidad', es: (d) => d.startsWith('psicomot') },
  { nombre: 'Kinesiología', es: (d) => d.startsWith('kine') },
  { nombre: 'Terapia Ocupacional', es: (d) => d.startsWith('terapia') || d === 'to' || d === 't.o.' },
];
const AREA_AGRUPADA = 'Kinesiología / Psicomotricidad / T.O.';
// Para separar una agenda con el área agrupada (respuestas anteriores al formulario actual).
const DISCIPLINAS_SEPARADAS = ['Kinesiología', 'Psicomotricidad', 'Terapia Ocupacional (T.O.)'];
const SIN_AREA = 'Sin área';
const ORDEN_AREAS = [...AREAS.map((a) => a.nombre), AREA_AGRUPADA, SIN_AREA];
function areaDe(disciplina) {
  const d = normalizar(disciplina);
  if (!d) return SIN_AREA;
  if (d.includes('/')) return AREA_AGRUPADA;
  return AREAS.find((a) => a.es(d))?.nombre || SIN_AREA;
}

// Número que se guarda al salir del campo (acepta coma decimal).
function CampoValor({ valor, alGuardar, etiqueta, placeholder = '', className = '' }) {
  const [texto, setTexto] = useState(valor == null ? '' : String(valor).replace('.', ','));
  useEffect(() => { setTexto(valor == null ? '' : String(valor).replace('.', ',')); }, [valor]);
  const guardar = () => {
    const limpio = texto.trim();
    const actual = valor == null ? '' : String(valor).replace('.', ',');
    if (limpio !== actual) alGuardar(limpio);
  };
  return (
    <input inputMode="decimal" aria-label={etiqueta} placeholder={placeholder} value={texto}
      onChange={(e) => setTexto(e.target.value)} onBlur={guardar} onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      className={`w-full min-w-[3.5rem] rounded border border-slate-300 bg-white px-1 py-0.5 text-right text-xs font-normal dark:border-slate-600 dark:bg-slate-800 ${className}`} />
  );
}

const PORCENTAJE_INICIAL = 90;
const pesos = (n) => `$ ${Math.round(n).toLocaleString('es-AR')}`;
const leerNumero = (texto) => (texto === '' || texto == null ? null : Number(String(texto).replace(',', '.')));

// Con admin, cada casilla se toca para abrir la agenda de ese profesional y
// corregirla: los números salen de sus horarios, así que se cambian ahí.
function PlanillaPacientesProfesionales({ admin = false, alCambiar = async () => {} }) {
  const [mes, setMes] = useState('');
  const [edicion, setEdicion] = useState(null);
  const [aviso, setAviso] = useState('');
  // Vista compacta: casillas chicas para ver todos los pacientes de una vez, como la planilla de Excel.
  const [compacta, setCompacta] = useState(false);
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');
  const [buscar, setBuscar] = useState('');
  const [buscarProfesional, setBuscarProfesional] = useState('');
  const [area, setArea] = useState('');
  const cargar = useCallback(async () => {
    try {
      setDatos(await obtenerPlanillaDobleEntradaApi(mes));
      setError('');
    } catch (err) {
      setError(String(err?.message || 'No se pudo cargar la planilla.'));
    }
  }, [mes]);
  useEffect(() => {
    cargar();
    const id = setInterval(cargar, 60 * 1000);
    return () => clearInterval(id);
  }, [cargar]);

  const [valores, setValores] = useState({ monto: null, profesionales: {} });
  const mesDatos = datos?.mes || '';
  useEffect(() => {
    if (!admin || !mesDatos) return;
    obtenerValoresAgendaApi(mesDatos).then((v) => setValores({ monto: v?.monto ?? null, profesionales: v?.profesionales || {} }))
      .catch(() => setValores({ monto: null, profesionales: {} }));
  }, [admin, mesDatos]);
  // Guarda un valor y lo deja puesto enseguida; si falla, avisa.
  const guardarValor = async (cambio) => {
    const { profesional, ...campos } = cambio;
    const numeros = Object.fromEntries(Object.entries(campos).map(([k, v]) => [k, leerNumero(v)]));
    if (Object.values(numeros).some((n) => n !== null && !Number.isFinite(n))) { setError('Escribí un número.'); return; }
    try {
      await guardarValoresAgendaApi({ mes: mesDatos, ...(profesional ? { profesional } : {}), ...campos });
      setError('');
      setValores((v) => (profesional
        ? { ...v, profesionales: { ...v.profesionales, [profesional]: { ...v.profesionales[profesional], ...numeros } } }
        : { ...v, ...numeros }));
    } catch (err) {
      setError(String(err?.message || 'No se pudo guardar el valor.'));
    }
  };
  const separar = async (profesional, disciplina) => {
    if (!disciplina) return;
    setAviso('');
    try {
      const r = await cambiarDisciplinaPlanillaApi(profesional.id, disciplina);
      setAviso(`${profesional.profesional} quedó en ${disciplina}${r?.casillas ? `, también en ${r.casillas} casilla${r.casillas === 1 ? '' : 's'} de la agenda general` : ''}.`);
      await cargar();
    } catch (err) {
      setError(String(err?.message || 'No se pudo cambiar la disciplina.'));
    }
  };
  const editar = async (profesional, paciente = '') => {
    if (!profesional.id) return;
    setAviso('');
    try {
      const detalle = await obtenerPlanillaProfesionalApi(profesional.id);
      setEdicion({ profesional, paciente, planilla: { id: profesional.id, profesional: profesional.profesional }, turnos: detalle.turnos || [] });
    } catch (err) {
      setError(String(err?.message || 'No se pudo abrir la agenda del profesional.'));
    }
  };

  // Profesionales agrupados por área (Fono, PSP, Psico, PSM, Kine, T.O.) y por nombre dentro de cada una.
  const todosProfesionales = (datos?.profesionales || [])
    .map((p) => ({ ...p, area: areaDe(p.disciplina) }))
    .sort((a, b) => ORDEN_AREAS.indexOf(a.area) - ORDEN_AREAS.indexOf(b.area) || a.profesional.localeCompare(b.profesional, 'es'));
  const areasPresentes = ORDEN_AREAS.filter((a) => todosProfesionales.some((p) => p.area === a));
  const filtroProfesional = normalizar(buscarProfesional);
  const profesionales = todosProfesionales.filter((p) => (!filtroProfesional || normalizar(p.profesional).includes(filtroProfesional))
    && (!area || p.area === area));
  const grupos = [];
  profesionales.forEach((p) => {
    if (grupos.at(-1)?.area !== p.area) grupos.push({ area: p.area, cantidad: 0, sesiones: 0 });
    grupos.at(-1).cantidad += 1;
    grupos.at(-1).sesiones += p.sesiones || 0;
  });
  const totalSesiones = profesionales.reduce((total, p) => total + (p.sesiones || 0), 0);
  // Línea gruesa a la izquierda del primer profesional de cada área.
  const inicioArea = new Set(profesionales.filter((p, i) => i === 0 || profesionales[i - 1].area !== p.area).map((p) => p.profesional));
  const separador = (p) => (inicioArea.has(p.profesional) ? 'border-l-4 border-l-cyan-400' : '');
  const celdaChica = compacta ? 'px-0.5 py-0 leading-none' : 'px-2';
  // Lo que cobra cada profesional: monto × porcentaje × 4 semanas × (sesiones + ajuste).
  const valorDe = (p) => valores.profesionales[p.profesional] || {};
  const porcentajeDe = (p) => valorDe(p).porcentaje ?? PORCENTAJE_INICIAL;
  const sesionesTotales = (p) => (p.sesiones || 0) + (valorDe(p).ajuste || 0);
  const cobra = (p) => (valores.monto == null ? null : valores.monto * (porcentajeDe(p) / 100) * 4 * sesionesTotales(p));
  const totalAjustes = profesionales.reduce((total, p) => total + (valorDe(p).ajuste || 0), 0);
  const totalCobra = profesionales.reduce((total, p) => total + (cobra(p) || 0), 0);
  const filtro = normalizar(buscar);
  // Con un área elegida, solo los pacientes que tienen algún profesional de esa área.
  const pacientes = (datos?.pacientes || []).filter((p) => (!filtro || normalizar(p.nombre).includes(filtro))
    && (!area || profesionales.some((x) => p.horarios[x.profesional])));
  const csv = () => {
    const celda = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const filas = [['Área', '', ...profesionales.map((p) => p.area)], ['Paciente', 'Profesionales', ...profesionales.map((p) => p.profesional)],
      ...pacientes.map((p) => [p.nombre, p.profesionales, ...profesionales.map((x) => (p.horarios[x.profesional] ? 1 : ''))]),
      ['Sesiones por semana', totalSesiones, ...profesionales.map((p) => p.sesiones || 0)],
      ...(admin ? [
        ['Ajuste a mano', totalAjustes, ...profesionales.map((p) => valorDe(p).ajuste ?? '')],
        ['Total de sesiones', totalSesiones + totalAjustes, ...profesionales.map(sesionesTotales)],
        ['Porcentaje', '', ...profesionales.map((p) => `${porcentajeDe(p)}%`)],
        ['Valor de la sesión', valores.monto ?? '', ...profesionales.map(() => '')],
        ['A cobrar', valores.monto == null ? '' : Math.round(totalCobra), ...profesionales.map((p) => (cobra(p) == null ? '' : Math.round(cobra(p))))],
      ] : [])];
    descargar(`\ufeff${filas.map((f) => f.map(celda).join(';')).join('\n')}`, `pacientes-profesionales-${datos?.mes || 'mes'}.csv`);
  };

  return (
    <section className={`${tarjeta} p-4 sm:p-6`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="mr-auto">
          <h3 className="text-lg font-extrabold text-slate-900 dark:text-white">Pacientes por profesional</h3>
          <p className="text-xs text-slate-500">Se marca solo con cada respuesta del formulario. En rojo, un paciente con dos tratamientos a la misma hora.</p>
        </div>
        <InputMes value={datos?.mes || mes} onChange={setMes} />
        <input className={inputClass} placeholder="Buscar paciente" aria-label="Buscar paciente" value={buscar} onChange={(e) => setBuscar(e.target.value)} />
        <input className={inputClass} placeholder="Buscar profesional" aria-label="Buscar profesional" value={buscarProfesional} onChange={(e) => setBuscarProfesional(e.target.value)} />
        <select className={inputClass} aria-label="Área" value={area} onChange={(e) => setArea(e.target.value)}>
          <option value="">Todas las áreas</option>
          {areasPresentes.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <button type="button" onClick={() => setCompacta((c) => !c)} aria-pressed={compacta}
          className={`rounded-xl border px-3 py-2 text-sm font-bold ${compacta ? 'border-emerald-600 bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-100' : 'border-slate-200 text-slate-700 dark:border-slate-600 dark:text-slate-200'}`}>
          {compacta ? 'Vista normal' : 'Vista compacta'}
        </button>
        <button type="button" onClick={cargar} className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold text-slate-700 dark:border-slate-600 dark:text-slate-200">Actualizar</button>
        <button type="button" onClick={csv} disabled={!pacientes.length} className="rounded-xl bg-emerald-600 px-3 py-2 text-sm font-bold text-white disabled:opacity-40">Descargar CSV</button>
      </div>
      {error && <p className="mt-3 text-sm font-semibold text-rose-600">{error}</p>}
      {admin && !edicion && <p className="mt-2 text-xs text-slate-500">Tocá una casilla o un profesional para corregir su agenda.</p>}
      {aviso && <p role="status" className="mt-3 text-sm font-semibold text-emerald-700">{aviso}</p>}
      {edicion && (
        <div className="mt-4 space-y-2 rounded-xl border border-emerald-300 bg-emerald-50/40 p-3 dark:border-emerald-800 dark:bg-emerald-950/30">
          <h4 className="font-bold text-slate-900 dark:text-white">
            Agenda de {edicion.profesional.profesional}{edicion.paciente ? ` · ${edicion.paciente} en verde` : ''}
          </h4>
          <EditorPlanilla planilla={edicion.planilla} turnos={edicion.turnos} resaltar={edicion.paciente}
            alCancelar={() => setEdicion(null)}
            alTerminar={async (mensaje) => {
              setEdicion(null);
              setAviso(mensaje);
              await Promise.all([cargar(), alCambiar()]);
            }} />
        </div>
      )}
      {datos && todosProfesionales.length > 0 && !profesionales.length && <p className="mt-6 text-center text-sm text-slate-500">Ningún profesional coincide con la búsqueda.</p>}
      {datos && !todosProfesionales.length && <p className="mt-6 text-center text-sm text-slate-500">Todavía no llegaron respuestas del formulario para este mes.</p>}
      {profesionales.length > 0 && (
        <div className={`mt-4 overflow-auto rounded-xl border border-slate-200 dark:border-slate-700 ${compacta ? '' : 'max-h-[70vh]'}`}>
          <table className={`border-collapse ${compacta ? 'text-[9px]' : 'text-xs'}`} data-compacta={compacta ? 'true' : undefined}>
            <thead className="sticky top-0 z-20 bg-slate-100 dark:bg-slate-900">
              <tr>
                <th colSpan={2} className="sticky left-0 z-30 border border-slate-300 bg-slate-100 px-2 py-1 text-left dark:border-slate-600 dark:bg-slate-900">Área</th>
                {grupos.map((g) => (
                  <th key={g.area} colSpan={g.cantidad} scope="colgroup"
                    className="border border-l-4 border-slate-400 border-l-cyan-400 bg-slate-200 px-2 py-1 text-left font-extrabold text-slate-800 dark:border-slate-500 dark:bg-slate-700 dark:text-slate-100">
                    {g.area} <span className="font-normal text-slate-500 dark:text-slate-300">· {g.cantidad} prof. · {g.sesiones} ses.</span>
                  </th>
                ))}
              </tr>
              <tr>
                <th className="sticky left-0 z-30 min-w-[14rem] border border-slate-300 bg-slate-100 px-2 py-1 text-left dark:border-slate-600 dark:bg-slate-900">Paciente ({pacientes.length})</th>
                <th className="border border-slate-300 px-2 py-1 dark:border-slate-600" title="Cantidad de profesionales">Prof.</th>
                {profesionales.map((p) => (
                  <th key={p.profesional} className={`${compacta ? 'max-w-[3rem] px-0.5 py-0.5 [writing-mode:vertical-rl] rotate-180' : 'min-w-[5.5rem] px-2 py-1'} border border-slate-300 text-left align-bottom dark:border-slate-600 ${separador(p)}`}
                    title={`${p.disciplina || 'Sin disciplina'} · ${ESTADO_PLANILLA[p.estado]?.texto || p.estado}`}>
                    {admin && p.id ? (
                      <button type="button" className="block text-left font-bold underline decoration-dotted"
                        onClick={() => editar(p)} aria-label={`Editar agenda de ${p.profesional}`}>{p.profesional}</button>
                    ) : <span className="block font-bold">{p.profesional}</span>}
                    {compacta ? null : admin && p.id && [AREA_AGRUPADA, SIN_AREA].includes(p.area) ? (
                      <select className="mt-0.5 block w-full rounded border border-amber-400 bg-amber-50 text-[11px] font-normal text-slate-800 dark:bg-amber-950 dark:text-slate-100"
                        aria-label={`Área de ${p.profesional}`} value="" onChange={(e) => separar(p, e.target.value)}>
                        <option value="">Elegir área…</option>
                        {DISCIPLINAS_SEPARADAS.map((d) => <option key={d} value={d}>{d}</option>)}
                      </select>
                    ) : <span className="block font-normal text-slate-500">{p.disciplina}</span>}
                    {!compacta && <span className="block font-normal text-slate-500">{p.pacientes} pac.</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pacientes.map((p) => (
                <tr key={p.nombre} className="hover:bg-slate-50 dark:hover:bg-slate-700/40">
                  <th scope="row" className={`sticky left-0 z-10 whitespace-nowrap border border-slate-300 bg-white text-left font-medium dark:border-slate-600 dark:bg-slate-800 ${compacta ? 'px-1 py-0 leading-tight' : 'px-2 py-0.5'}`}>{p.nombre}</th>
                  <td className={`border border-slate-300 bg-yellow-200 text-center font-bold text-slate-900 dark:border-slate-600 ${celdaChica}`}>{p.profesionales}</td>
                  {profesionales.map((x) => {
                    const tiene = Boolean(p.horarios[x.profesional]);
                    const pisado = tiene && (p.pisados || []).includes(x.profesional);
                    return (
                      <td key={x.profesional} className={`border border-slate-300 text-right dark:border-slate-600 ${celdaChica} ${compacta ? 'min-w-[1rem]' : ''} ${separador(x)} ${pisado ? 'bg-red-500 font-bold text-white' : tiene ? 'bg-green-500 font-bold text-slate-900' : ''}`}
                        title={tiene ? `${p.horarios[x.profesional]} ${p.horarios[x.profesional] === 1 ? 'sesión' : 'sesiones'} por semana` : undefined}
                        aria-label={tiene && !admin ? `${p.nombre} con ${x.profesional}${pisado ? ', se pisa con otro tratamiento' : ''}` : undefined}>
                        {admin && x.id ? (
                          <button type="button" className={`block h-full w-full text-right ${compacta ? '' : 'min-h-[1.25rem]'}`}
                            aria-label={`Editar ${p.nombre} con ${x.profesional}${pisado ? ', se pisa con otro tratamiento' : ''}`}
                            onClick={() => editar(x, p.nombre)}>{tiene ? 1 : ''}</button>
                        ) : (tiene ? 1 : '')}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
            {/* Pie: sesiones por semana de cada profesional y el total. */}
            <tfoot className={`${admin ? '' : 'sticky bottom-0 z-20'} bg-slate-100 font-bold dark:bg-slate-900`}>
              <tr>
                <th scope="row" className="sticky left-0 z-30 border border-slate-300 bg-slate-100 px-2 py-1 text-left dark:border-slate-600 dark:bg-slate-900">Sesiones por semana</th>
                <td className="border border-slate-300 px-2 py-1 text-center dark:border-slate-600" aria-label={`Total: ${totalSesiones} sesiones`}>{totalSesiones}</td>
                {profesionales.map((p) => (
                  <td key={p.profesional} className={`border border-slate-300 px-2 py-1 text-right dark:border-slate-600 ${separador(p)}`}
                    aria-label={`${p.profesional}: ${p.sesiones || 0} sesiones`}>{p.sesiones || 0}</td>
                ))}
              </tr>
              {admin && (() => {
                const celdaTitulo = 'sticky left-0 z-30 border border-slate-300 bg-slate-100 px-2 py-1 text-left dark:border-slate-600 dark:bg-slate-900';
                const celda = 'border border-slate-300 px-1 py-0.5 text-right dark:border-slate-600';
                return (
                  <>
                    <tr>
                      <th scope="row" className={celdaTitulo}>Ajuste a mano <span className="font-normal text-slate-500">(ej. 0,5)</span></th>
                      <td className={`${celda} text-center`}>{totalAjustes || ''}</td>
                      {profesionales.map((p) => (
                        <td key={p.profesional} className={celda}>
                          <CampoValor valor={valorDe(p).ajuste} etiqueta={`Ajuste de ${p.profesional}`}
                            alGuardar={(v) => guardarValor({ profesional: p.profesional, ajuste: v })} />
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <th scope="row" className={celdaTitulo}>Total de sesiones</th>
                      <td className={`${celda} text-center`}>{totalSesiones + totalAjustes}</td>
                      {profesionales.map((p) => (
                        <td key={p.profesional} className={celda} aria-label={`Total de ${p.profesional}: ${sesionesTotales(p)} sesiones`}>
                          {String(sesionesTotales(p)).replace('.', ',')}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <th scope="row" className={celdaTitulo}>Porcentaje</th>
                      <td className={celda} />
                      {profesionales.map((p) => (
                        <td key={p.profesional} className={celda}>
                          <div className="flex items-center gap-0.5">
                            <CampoValor valor={valorDe(p).porcentaje} placeholder={String(PORCENTAJE_INICIAL)} etiqueta={`Porcentaje de ${p.profesional}`}
                              alGuardar={(v) => guardarValor({ profesional: p.profesional, porcentaje: v })} />%
                          </div>
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <th scope="row" className={celdaTitulo}>Valor de la sesión del mes</th>
                      <td className={celda} colSpan={Math.min(2, profesionales.length + 1)}>
                        <CampoValor valor={valores.monto} placeholder="$" etiqueta="Valor de la sesión del mes" className="min-w-[6rem]"
                          alGuardar={(v) => guardarValor({ monto: v })} />
                      </td>
                      {profesionales.length > 1 && <td className={celda} colSpan={profesionales.length - 1} />}
                    </tr>
                    <tr>
                      <th scope="row" className={celdaTitulo} title="Valor × porcentaje × 4 × total de sesiones">A cobrar</th>
                      <td className={`${celda} whitespace-nowrap text-center`} aria-label={`A cobrar en total: ${valores.monto == null ? 'sin valor' : pesos(totalCobra)}`}>
                        {valores.monto == null ? '' : pesos(totalCobra)}
                      </td>
                      {profesionales.map((p) => (
                        <td key={p.profesional} className={`${celda} whitespace-nowrap`}
                          aria-label={`${p.profesional} cobra ${cobra(p) == null ? 'sin valor' : pesos(cobra(p))}`}>
                          {cobra(p) == null ? '' : pesos(cobra(p))}
                        </td>
                      ))}
                    </tr>
                  </>
                );
              })()}
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
}

const VISTAS = [
  { key: 'dia', label: 'Agenda del día', icon: 'fact_check' },
  { key: 'semana', label: 'Agenda general', icon: 'calendar_view_week' },
  { key: 'alertas', label: 'Alertas', icon: 'warning' },
  { key: 'profesionales', label: 'Profesionales', icon: 'assignment_ind' },
  { key: 'cruce', label: 'Pacientes por profesional', icon: 'grid_on' },
  { key: 'configurar', label: 'Configurar', icon: 'settings', admin: true },
];

export default function Agendas({ usuario }) {
  const { pacientes } = usePacientes();
  const [vista, setVista] = useState('dia');
  const [sedes, setSedes] = useState(null);
  const [sedeId, setSedeId] = useState('');
  const [error, setError] = useState('');

  const cargarSedes = useCallback(async () => {
    try {
      const lista = ordenarCentros(await obtenerSedesAgendaApi());
      setSedes(lista);
      setSedeId((actual) => (lista.some((s) => String(s.id) === actual) ? actual : String(lista[0]?.id || '')));
    } catch (err) {
      setSedes([]);
      setError(String(err?.message || 'No se pudieron cargar los centros.'));
    }
  }, []);
  useEffect(() => { cargarSedes(); }, [cargarSedes]);
  const [pendientes, setPendientes] = useState(0);
  const contarPendientes = useCallback(async () => {
    try {
      setPendientes(Number((await contarPlanillasPendientesApi())?.pendientes || 0));
    } catch { /* el aviso es opcional */ }
  }, []);
  useEffect(() => { contarPendientes(); }, [contarPendientes]);

  const listaPacientes = useMemo(() => (pacientes || []).filter((p) => !p.dadoDeBaja), [pacientes]);
  const vistas = VISTAS.filter((v) => !v.admin || usuario?.isAdmin);
  const sinAgenda = sedes && !sedes.length;

  return (
    <main className="p-4 sm:p-6 lg:p-8">
      {/* La agenda general usa todo el ancho para que, al alejar, entren más consultorios por fila. */}
      <div className={`mx-auto space-y-6 ${vista === 'semana' ? 'max-w-none' : 'max-w-7xl'}`}>
        <div className="print:hidden">
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-emerald-700">Agendas</p>
          <h2 className="mt-1 text-3xl font-extrabold text-slate-900 dark:text-white">Agenda por centro y consultorio</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2 print:hidden" role="tablist">
          {vistas.map((v) => (
            <button key={v.key} type="button" role="tab" aria-selected={vista === v.key} onClick={() => setVista(v.key)}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold ${vista === v.key ? 'bg-emerald-600 text-white' : 'border border-slate-200 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200'}`}>
              <span className="material-symbols-outlined text-base">{v.icon}</span>{v.label}
              {v.key === 'profesionales' && pendientes > 0 && (
                <span className="rounded-full bg-amber-400 px-2 text-xs text-slate-900" aria-label={`${pendientes} pendientes de aprobación`}>{pendientes}</span>
              )}
            </button>
          ))}
          {vista === 'dia' && sedes?.length > 0 && (
            <select className={`${inputClass} ml-auto`} value={sedeId} onChange={(e) => setSedeId(e.target.value)} aria-label="Centro">
              {sedes.map((s) => <option key={s.id} value={String(s.id)}>{s.nombre}</option>)}
            </select>
          )}
        </div>
        {error && <p className="text-sm font-semibold text-rose-600">{error}</p>}
        {sinAgenda && vista !== 'configurar' && (
          <div className={`${tarjeta} p-10 text-center text-sm text-slate-500`}>
            Todavía no hay agenda cargada. {usuario?.isAdmin ? 'Cargala desde Configurar con el CSV de la planilla.' : 'Un administrador tiene que cargarla.'}
          </div>
        )}
        {!sinAgenda && vista === 'dia' && sedeId && <PlanillaDia sedeId={sedeId} />}
        {!sinAgenda && vista === 'semana' && sedes?.length > 0 && <AgendaSemanal sedes={sedes} pacientes={listaPacientes} editable />}
        {!sinAgenda && vista === 'alertas' && sedes?.length > 0 && (
          <Alertas sedes={sedes} pacientes={listaPacientes} editable irAProfesionales={() => setVista('profesionales')} />
        )}
        {vista === 'profesionales' && <AgendasProfesionales admin={Boolean(usuario?.isAdmin)} alCambiar={contarPendientes} />}
        {vista === 'cruce' && <PlanillaPacientesProfesionales admin={Boolean(usuario?.isAdmin)} alCambiar={contarPendientes} />}
        {vista === 'configurar' && usuario?.isAdmin && <Configuracion sedes={sedes || []} alCambiar={cargarSedes} />}
      </div>
    </main>
  );
}
